<template>
  <div class="c1-main c1-shifts-page" @click="c1TopTab = null">

    <!-- TOAST -->
    <transition name="sh1-toast-fade">
      <div v-if="toast" class="sh1-toast" :style="{ background: toast.color }">{{ toast.msg }}</div>
    </transition>

    <!-- IDLE WARNING OVERLAY -->
    <transition name="s1-fade">
      <div v-if="idleWarning" class="s1-idle-overlay">
        <div class="s1-idle-box">
          <div class="s1-idle-icon">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div class="s1-idle-title">Are you still here?</div>
          <div class="s1-idle-sub">No activity detected for 60 minutes. Click to confirm presence or you will be logged out.</div>
          <button class="s1-idle-btn" @click="confirmPresence">I'm here</button>
          <div class="s1-idle-countdown">Auto-logout in {{ idleCountdown }}s</div>
        </div>
      </div>
    </transition>


    <!-- ═══════════════════════════════════════════ TOP BAR ═══ -->
    <div class="c1-sticky-top">
    <div class="c1-tib" @click="panel = null; s1VikiOpen = false; c1HamMenuOpen = false; c1ShiftMenuOpen = false; c1TopTab = null">
      <!-- LEFT 25% — Viki face -->
      <div class="c1-tib-side c1-tib-left" style="padding-left:0;gap:8px;">
        <div class="c1-viki-topface"
          :class="{ 'c1-viki-topface--active': s1VikiOpen }"
          @click.stop="s1VikiOpen = !s1VikiOpen"
        >
          <viki-face :mood="c1VikiMood" :speaking="c1VikiSpeaking" :size="44" style="width:72px;height:44px;display:block;overflow:hidden;border-radius:0;"/>
        </div>
        <div class="c1-shift-wrap" @click.stop="c1ShiftMenuOpen = !c1ShiftMenuOpen; c1HamMenuOpen = false">
          <div class="c1-tib-shift-pill" :class="{ open: c1ShiftMenuOpen }">
            ⏱ {{ c1ShiftElapsed }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px;margin-left:3px;transition:transform .2s" :style="{ transform: c1ShiftMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>
      <!-- CENTER 50% — module title only -->
      <div class="c1-tib-center">
        <div class="c1-tib-mod-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Shifts
        </div>
      </div>
      <!-- RIGHT 25% — ham only -->
      <div class="c1-tib-side c1-tib-right">
        <div class="c1-ham-wrap" @click.stop>
          <button class="c1-ham-btn" :class="{ open: c1HamMenuOpen }" @click="c1HamMenuOpen = !c1HamMenuOpen; c1ShiftMenuOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:18px;height:18px"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>
    </div>
    <!-- SHIFT DRAWER -->
    <div class="c1-top-drawer c1-shift-drawer" :class="{ open: c1ShiftMenuOpen }" @click.stop>
      <div class="c1-td-inner">
        <div class="c1-td-section-lbl">My Shift</div>
        <div class="c1-td-status-row">
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono">{{ c1ShiftStartTime ? new Date(c1ShiftStartTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' }}</div>
            <div class="c1-td-stat-lbl">Started</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono" style="color:var(--c1-dp)">{{ c1ShiftElapsed }}</div>
            <div class="c1-td-stat-lbl">Elapsed</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1BathroomCount }}×</div>
            <div class="c1-td-stat-lbl">🚽 Bathroom</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1CoffeeCount }}×</div>
            <div class="c1-td-stat-lbl">☕ Coffee</div>
          </div>
        </div>
        <div class="c1-td-btn-row">
          <button class="c1-td-btn c1-td-btn-shift"   :class="{ active: c1Status === 'shift' }"   @click.stop="c1SetStatus('shift')">▶ On Shift</button>
          <button class="c1-td-btn c1-td-btn-bathroom" :class="{ active: c1Status === 'bathroom' }" @click.stop="c1SetStatus('bathroom')">🚽 Bathroom</button>
          <button class="c1-td-btn c1-td-btn-coffee"  :class="{ active: c1Status === 'coffee' }"  @click.stop="c1SetStatus('coffee')">☕ Coffee</button>
          <button class="c1-td-btn c1-td-btn-break"   :class="{ active: c1Status === 'break' }"   @click.stop="c1SetStatus('break')">⏸ Break</button>
          <button class="c1-td-btn c1-td-btn-lunch"   :class="{ active: c1Status === 'lunch' }"   @click.stop="c1SetStatus('lunch')">🍽 Lunch</button>
          <button class="c1-td-btn c1-td-btn-end"     @click.stop="c1SetStatus('end')">⏹ End Shift</button>
        </div>
      </div>
    </div>
    <!-- HAM NAV DRAWER -->
    <div class="c1-top-drawer c1-ham-drawer" :class="{ open: c1HamMenuOpen }" @click.stop>
      <div class="c1-td-inner c1-td-nav-grid">
        <router-link to="/clients1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-dp)"></span>Clients</router-link>
        <router-link to="/tasks1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-sc)"></span>Tasks</router-link>
        <router-link to="/stats1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-ld)"></span>Statistics</router-link>
        <router-link to="/chat1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-rf)"></span>Chat</router-link>
        <router-link to="/communications1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#3b82f6"></span>Communications</router-link>
        <router-link to="/affiliation1" class="c1-td-nav-item" :class="{ on: $route.path === '/affiliation1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#8b5cf6"></span>Affiliation</router-link>
        <router-link to="/finance1" class="c1-td-nav-item" :class="{ on: $route.path === '/finance1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#10b981"></span>Finance</router-link>
        <router-link to="/shifts1" class="c1-td-nav-item" :class="{ on: $route.path === '/shifts1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#f59e0b"></span>Shifts</router-link>
        <router-link to="/users1" class="c1-td-nav-item" :class="{ on: $route.path === '/users1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#06b6d4"></span>Users</router-link>
        <router-link to="/brokers1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ec4899"></span>Brokers</router-link>
        <router-link to="/security1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ef4444"></span>Security</router-link>
        <router-link to="/quiz1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#a78bfa"></span>Quiz</router-link>
        <router-link to="/instruction1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#64748b"></span>Instructions</router-link>
        <router-link to="/settings1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#94a3b8"></span>Settings</router-link>
        <router-link to="/profile1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#22d3ee"></span>My Profile</router-link>
      </div>
    </div>
    </div><!-- /c1-sticky-top -->

    <!-- TOP STRIP -->
    <div class="c1-top-strip c1-mod-strip" @click.stop>
      <div class="c1-top-strip-body">
        <div class="c1-ts-tab-row">

          <!-- Agent/Search tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'agent'}" @click.stop="c1TopTab = c1TopTab === 'agent' ? null : 'agent'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            <span>Agent</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='agent' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>

          <!-- Date tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'date'}" @click.stop="c1TopTab = c1TopTab === 'date' ? null : 'date'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>Date</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='date' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>

          <!-- Status tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'status'}" @click.stop="c1TopTab = c1TopTab === 'status' ? null : 'status'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span>Status</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='status' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>

          <!-- Shift Type tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'shifttype'}" @click.stop="c1TopTab = c1TopTab === 'shifttype' ? null : 'shifttype'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>Shift</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='shifttype' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>

          <!-- Role tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'role'}" @click.stop="c1TopTab = c1TopTab === 'role' ? null : 'role'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
            <span>Role</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='role' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>

          <!-- Charts tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'charts'}" @click.stop="c1TopTab = c1TopTab === 'charts' ? null : 'charts'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <span>Charts</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='charts' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>


        </div>

        <!-- Agent dropdown -->
        <div v-show="c1TopTab === 'agent'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-lbl">Search agent</div>
          <div class="c1-fp-search">
            <svg class="c1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input class="c1-fp-sinput" v-model.trim="filterSearch" placeholder="Name…" type="text" />
            <div v-if="filterSearch" class="c1-fp-sclear" @click="filterSearch = ''">
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          </div>
        </div>

        <!-- Date dropdown -->
        <div v-show="c1TopTab === 'date'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-lbl">Date range</div>
          <div class="c1-fp-opts">
            <div class="c1-fp-opt" :class="{ on: datePreset === 'today' }" @click="setDatePreset('today')">Today</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'yesterday' }" @click="setDatePreset('yesterday')">Yesterday</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'tw' }" @click="setDatePreset('tw')">This week</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'lw' }" @click="setDatePreset('lw')">Last week</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'tm' }" @click="setDatePreset('tm')">This month</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'lm' }" @click="setDatePreset('lm')">Last month</div>
            <div class="c1-fp-opt" :class="{ on: datePreset === 'all' }" @click="setDatePreset('all')">All time</div>
          </div>
          <div class="c1-fp-lbl" style="margin-top:10px">Week</div>
          <div class="c1-fp-opts">
            <div class="c1-fp-opt" :class="{ on: selectedTab === 'shiftsThisWeek' }" @click="selectTab('shiftsThisWeek')">This week</div>
            <div class="c1-fp-opt" :class="{ on: selectedTab === 'shiftsNextWeek' }" @click="selectTab('shiftsNextWeek')">Next week</div>
          </div>
        </div>

        <!-- Status dropdown -->
        <div v-show="c1TopTab === 'status'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-lbl">Attendance status</div>
          <div class="c1-fp-opts">
            <div class="c1-fp-opt" :class="{ on: filterStatuses.length === 0 }" @click="filterStatuses = []">All</div>
            <div class="c1-fp-opt s1-chip-on-time" :class="{ on: filterStatuses.includes('on_time') }" @click="toggleStatus('on_time')">On Time</div>
            <div class="c1-fp-opt s1-chip-late" :class="{ on: filterStatuses.includes('late') }" @click="toggleStatus('late')">Late</div>
            <div class="c1-fp-opt s1-chip-absent" :class="{ on: filterStatuses.includes('absent') }" @click="toggleStatus('absent')">Absent</div>
            <div class="c1-fp-opt s1-chip-early" :class="{ on: filterStatuses.includes('early_bird') }" @click="toggleStatus('early_bird')">Early Bird</div>
            <div class="c1-fp-opt s1-chip-none" :class="{ on: filterStatuses.includes('not_on_shift') }" @click="toggleStatus('not_on_shift')">Not on shift</div>
          </div>
        </div>

        <!-- Shift Type dropdown -->
        <div v-show="c1TopTab === 'shifttype'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-lbl">Shift type</div>
          <div class="c1-fp-opts">
            <div class="c1-fp-opt" :class="{ on: filterShiftType === '' }" @click="filterShiftType = ''">All</div>
            <div class="c1-fp-opt s1-b-nit" :class="{ on: filterShiftType === 'night' }" @click="filterShiftType = 'night'">Night 23–09</div>
            <div class="c1-fp-opt s1-b-mor" :class="{ on: filterShiftType === 'morning' }" @click="filterShiftType = 'morning'">Morning 09–17</div>
            <div class="c1-fp-opt s1-b-day" :class="{ on: filterShiftType === 'day' }" @click="filterShiftType = 'day'">Day 11–19</div>
            <div class="c1-fp-opt s1-b-eve" :class="{ on: filterShiftType === 'evening' }" @click="filterShiftType = 'evening'">Evening 15–23</div>
            <div class="c1-fp-opt s1-b-vac" :class="{ on: filterShiftType === 'vacation' }" @click="filterShiftType = 'vacation'">Vacation</div>
          </div>
        </div>

        <!-- Role dropdown -->
        <div v-show="c1TopTab === 'role'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-lbl">Role</div>
          <div class="c1-fp-opts">
            <div class="c1-fp-opt" :class="{ on: filterRole === '' }" @click="filterRole = ''">All</div>
            <div class="c1-fp-opt" :class="{ on: filterRole === 'agent' }" @click="filterRole = 'agent'">Agent</div>
            <div class="c1-fp-opt" :class="{ on: filterRole === 'manager' }" @click="filterRole = 'manager'">Manager</div>
            <div class="c1-fp-opt" :class="{ on: filterRole === 'admin' }" @click="filterRole = 'admin'">Admin</div>
          </div>
        </div>

        <!-- Charts dropdown -->
        <div v-show="c1TopTab === 'charts'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-co-lbl">Shift coverage this week</div>
          <div class="c1-kpi-row">
            <div class="c1-kpi"><div class="c1-kpi-lbl">Night</div><div class="c1-kpi-val s1-c-nit">{{ sumNight || 0 }}</div></div>
            <div class="c1-kpi"><div class="c1-kpi-lbl">Morning</div><div class="c1-kpi-val s1-c-mor">{{ sumMorning || 0 }}</div></div>
            <div class="c1-kpi"><div class="c1-kpi-lbl">Day</div><div class="c1-kpi-val s1-c-day">{{ sumDay || 0 }}</div></div>
            <div class="c1-kpi"><div class="c1-kpi-lbl">Evening</div><div class="c1-kpi-val s1-c-eve">{{ sumEvening || 0 }}</div></div>
            <div class="c1-kpi"><div class="c1-kpi-lbl">Total</div><div class="c1-kpi-val">{{ rawShifts.length }}</div></div>
          </div>
          <div class="c1-co-lbl" style="margin-top:14px">Punctuality rate per agent</div>
          <div class="s1-punct-agent-bars">
            <div v-for="(a, i) in punctualityPerAgent" :key="i" class="s1-pab-row">
              <div class="s1-pab-av" :style="{ background: userColor(a.name) }">{{ initials(a.name) }}</div>
              <div class="s1-pab-name">{{ shortName(a.name) }}</div>
              <div class="s1-pab-track"><div class="s1-pab-fill" :style="{ width: a.pct + '%', background: punctColor(a.pct) }"></div></div>
              <div class="s1-pab-val" :style="{ color: punctColor(a.pct) }">{{ a.pct }}%</div>
            </div>
            <div v-if="!punctualityPerAgent.length" class="s1-pab-empty">No punctuality data yet</div>
          </div>
          <div class="c1-co-lbl" style="margin-top:14px">Shift type distribution</div>
          <div class="c1-vbar-chart">
            <div class="c1-vbar-row"><div class="c1-vbar-lbl">Night</div><div class="c1-vbar-track"><div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumNight) + '%', background: '#7c3aed' }"><span class="c1-vbar-val">{{ sumNight }}</span></div></div></div>
            <div class="c1-vbar-row"><div class="c1-vbar-lbl">Morning</div><div class="c1-vbar-track"><div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumMorning) + '%', background: '#2563eb' }"><span class="c1-vbar-val">{{ sumMorning }}</span></div></div></div>
            <div class="c1-vbar-row"><div class="c1-vbar-lbl">Day</div><div class="c1-vbar-track"><div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumDay) + '%', background: '#16a34a' }"><span class="c1-vbar-val">{{ sumDay }}</span></div></div></div>
            <div class="c1-vbar-row"><div class="c1-vbar-lbl">Evening</div><div class="c1-vbar-track"><div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumEvening) + '%', background: '#ea580c' }"><span class="c1-vbar-val">{{ sumEvening }}</span></div></div></div>
          </div>
        </div>

        <div class="c1-nm-div"></div>
        <c1-mod-calendar :items="rawShifts" dateField="start_time" itemLabel="shifts" style="flex-shrink:0" />

      </div>
    </div>
    <!-- /TOP STRIP -->

    <!-- Viki left panel backdrop -->
    <transition name="c1-bd-fade">
      <div v-if="s1VikiOpen" class="c1-panel-backdrop" @click.stop="s1VikiOpen = false"></div>
    </transition>

    <!-- VIKI LEFT PANEL -->
    <transition name="s1-viki-slide">
      <div v-if="s1VikiOpen" class="s1-viki-left-panel" @click.stop>
        <div class="c1-panel-hdr-bar">
          <span class="c1-panel-hdr-ttl">Viki AI</span>
          <div class="c1-panel-back-btn" @click="s1VikiOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </div>
        </div>
        <div class="c1-sl-body">
          <div class="s1-viki-hdr" style="padding:0 0 12px">
            <div class="s1-viki-icon">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            </div>
            <div>
              <div class="s1-viki-title">Viki <span class="s1-viki-badge">AI</span></div>
              <div class="s1-viki-sub">Shift intelligence assistant</div>
            </div>
          </div>
          <div v-if="lateAlerts.length" class="s1-viki-section">
            <div class="s1-viki-section-lbl s1-viki-lbl-warn">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Late arrivals
            </div>
            <div v-for="(a, i) in lateAlerts" :key="'late' + i" class="s1-viki-alert-row s1-alert-late">
              <div class="s1-va-av" :style="{ background: userColor(a.name) }">{{ initials(a.name) }}</div>
              <div class="s1-va-text"><strong>{{ a.name }}</strong> is <strong>{{ a.minutesLate }} min late</strong> for {{ a.shiftType }} shift</div>
              <span class="s1-va-time">{{ a.time }}</span>
            </div>
          </div>
          <div v-if="noShowAlerts.length" class="s1-viki-section">
            <div class="s1-viki-section-lbl s1-viki-lbl-danger">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              No-shows
            </div>
            <div v-for="(a, i) in noShowAlerts" :key="'ns' + i" class="s1-viki-alert-row s1-alert-absent">
              <div class="s1-va-av" :style="{ background: userColor(a.name) }">{{ initials(a.name) }}</div>
              <div class="s1-va-text"><strong>{{ a.name }}</strong> has not clocked in for {{ a.shiftType }} shift</div>
              <span class="s1-va-time">{{ a.time }}</span>
            </div>
          </div>
          <div v-if="switchNotifications.length" class="s1-viki-section">
            <div class="s1-viki-section-lbl s1-viki-lbl-info">
              <svg viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
              Switch requests
            </div>
            <div v-for="(n, i) in switchNotifications" :key="'sw' + i" class="s1-viki-notif-row">
              <div class="s1-vn-text"><strong>{{ n.from }}</strong> wants to switch with you on <strong>{{ n.day }}</strong></div>
              <div class="s1-vn-actions">
                <button class="s1-vn-btn s1-vn-yes" @click="respondSwitch(n, 'accept')">Let's switch</button>
                <button class="s1-vn-btn s1-vn-no" @click="respondSwitch(n, 'decline')">I can't</button>
                <button class="s1-vn-btn s1-vn-later" @click="respondSwitch(n, 'later')">In 3 hours</button>
              </div>
            </div>
          </div>
          <div v-if="!lateAlerts.length && !noShowAlerts.length && !switchNotifications.length" class="s1-viki-clear">
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
            <div>All good! No alerts right now.</div>
          </div>
          <div v-if="openSlots.length && !isManager" class="s1-viki-section">
            <div class="s1-viki-section-lbl s1-viki-lbl-info">
              <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Open shift slots — claim first!
            </div>
            <div v-for="(slot, i) in openSlots" :key="'slot' + i" class="s1-viki-slot-row">
              <span class="s1-slot-day">{{ slot.day }}</span>
              <span class="s1-status-chip" :class="'s1-b-' + slot.cls">{{ slot.shiftType }}</span>
              <span class="s1-slot-time">{{ slot.time }}</span>
              <button class="s1-slot-claim" @click="selfFillSlot(slot)">Claim</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- PANEL BACKDROP (only for content panels, not nav menu) -->
    <transition name="c1-bd-fade">
      <div v-if="panel" class="c1-panel-backdrop" @click.stop="panel = null"></div>
    </transition>

    <!-- UNIFIED PANEL -->
    <div class="c1-panel" :class="{ 'c1-panel--open': s1PanelIsOpen }" @click.capture="s1FilterPanelClick">
      <div class="c1-panel-track" :class="{ 'at-content': s1PanelAtContent }">

        <!-- SCREEN 0: NAV -->
        <div class="c1-panel-screen">
          <!-- User strip -->
          <div class="c1-nav-user-strip">
            <div class="c1-nav-av">
              {{ c1UserInitials }}
              <span class="c1-nav-av-dot" :class="'c1-dst-' + c1Status"></span>
            </div>
            <div class="c1-nav-uinfo">
              <div class="c1-nav-uname">{{ c1UserFullName }}</div>
              <div class="c1-nav-urole">{{ c1RoleLabel }}</div>
            </div>
            <div class="c1-nav-status-wrap" @click.stop="c1StatusMenuOpen = !c1StatusMenuOpen">
              <div class="c1-nav-status-btn" :class="'c1-st-' + c1Status">
                <span class="c1-nav-status-sdot"></span>
                <span class="c1-nav-status-elapsed">{{ c1StatusElapsed }}</span>
              </div>
              <transition name="c1-sfade">
                <div v-if="c1StatusMenuOpen" class="c1-nav-status-menu">
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('available')"><span class="c1-nav-status-opt-dot c1-st-dot-available"></span>Available</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('bathroom')"><span class="c1-nav-status-opt-dot c1-st-dot-bathroom"></span>Bathroom</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('coffee')"><span class="c1-nav-status-opt-dot c1-st-dot-coffee"></span>Coffee break</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('shift')"><span class="c1-nav-status-opt-dot c1-st-dot-shift"></span>Start shift</div>
                  <div class="c1-nav-status-opt c1-sto-end" @click.stop="c1SetStatus('end')"><span class="c1-nav-status-opt-dot c1-st-dot-end"></span>End shift</div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Timezone bar -->
          <div class="c1-nav-tz-bar">
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇬🇧</span><span class="c1-nav-tz-time">{{ c1TzUK }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇮🇱</span><span class="c1-nav-tz-time">{{ c1TzIL }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🏝</span><span class="c1-nav-tz-time">{{ c1TzCB }}</span></div>
          </div>

          <!-- ── NAVIGATE: all pages ── -->
          <div v-if="c1NavMode === 'navigate'" class="c1-sl-body" style="padding:6px 0 12px">
            <div class="c1-psel-sub c1-psel-sub-back" @click.stop="c1NavMode = 'panels'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><polyline points="15 18 9 12 15 6"/></svg>
              <span class="c1-psel-sub-lbl">{{ c1CurrentPageLabel }}</span>
            <span class="c1-nav-x-btn" @click.stop="panel = null"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;display:block"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span></div>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">CRM</div>
            <router-link class="c1-psel-sub" to="/clients1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Clients</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/tasks1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Tasks</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/stats1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Statistics</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/chat1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Chat</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/communications1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Communications</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/affiliation1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Affiliation</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Sales</div>
            <router-link class="c1-psel-sub" to="/finance1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Finance</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/shifts1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Shifts</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/games1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Games</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Team</div>
            <router-link class="c1-psel-sub" to="/users1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Users</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/brokers1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Brokers</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/security1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Security</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Admin</div>
            <router-link class="c1-psel-sub" to="/quiz1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Quiz</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/instruction1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Instructions</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/settings1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Settings</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/profile1" @click.native="panel = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">My Profile</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
          </div>

          <!-- Panels mode -->
          <div v-else class="c1-sl-body" style="padding:6px 0 12px">
            <!-- Navigate row -->
            <div class="c1-psel-sub c1-psel-sub-navigate" @click.stop="c1NavMode = 'navigate'">
              <span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span>
              <span class="c1-psel-sub-lbl" style="font-weight:600;color:var(--c1-dp)">{{ c1CurrentPageLabel }}</span>
              <span class="c1-psel-sub-hint">Go to another page</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-sub" @click="s1SelectPanel('filters')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Filters</span>
              <span v-if="activeFilterCount" class="c1-psel-sub-hint">{{ activeFilterCount }} active</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click="s1SelectPanel('charts')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Charts</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click="s1SelectPanel('calendar')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Calendar</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>

          </div><!-- end v-else panels -->
        </div>

        <!-- SCREEN 1: CONTENT -->
        <div class="c1-panel-screen">
          <div class="c1-panel-hdr-bar">
            <div class="c1-panel-back-btn" @click="panel = 'nav'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </div>
            <span class="c1-panel-hdr-ttl">{{ s1ActivePanelLabel }}</span>
            <div class="c1-panel-back-btn" @click="panel = null">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          </div>
          <div class="c1-sl-body">

            <!-- ═══ FILTERS CONTENT ═══ -->
            <template v-if="s1PanelActiveContent === 'filters'">

              <!-- Date range presets -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Date range</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'today' }" @click="setDatePreset('today')">Today</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'yesterday' }" @click="setDatePreset('yesterday')">Yesterday</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'tw' }" @click="setDatePreset('tw')">This week</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'lw' }" @click="setDatePreset('lw')">Last week</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'tm' }" @click="setDatePreset('tm')">This month</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'lm' }" @click="setDatePreset('lm')">Last month</div>
                  <div class="c1-fp-opt" :class="{ on: datePreset === 'all' }" @click="setDatePreset('all')">All time</div>
                </div>
              </div>

              <!-- Week selector -->
              <div class="c1-fp-group">
                <div class="c1-fp-lbl">Week</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: selectedTab === 'shiftsThisWeek' }" @click="selectTab('shiftsThisWeek')">This week</div>
                  <div class="c1-fp-opt" :class="{ on: selectedTab === 'shiftsNextWeek' }" @click="selectTab('shiftsNextWeek')">Next week</div>
                </div>
              </div>

              <!-- Role -->
              <div class="c1-fp-group">
                <div class="c1-fp-lbl">Role</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: filterRole === '' }" @click="filterRole = ''">All</div>
                  <div class="c1-fp-opt" :class="{ on: filterRole === 'agent' }" @click="filterRole = 'agent'">Agent</div>
                  <div class="c1-fp-opt" :class="{ on: filterRole === 'manager' }" @click="filterRole = 'manager'">Manager</div>
                  <div class="c1-fp-opt" :class="{ on: filterRole === 'admin' }" @click="filterRole = 'admin'">Admin</div>
                </div>
              </div>

              <!-- Attendance status -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Attendance status</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: filterStatuses.length === 0 }" @click="filterStatuses = []">All</div>
                  <div class="c1-fp-opt s1-chip-on-time"  :class="{ on: filterStatuses.includes('on_time') }"    @click="toggleStatus('on_time')">On Time</div>
                  <div class="c1-fp-opt s1-chip-late"     :class="{ on: filterStatuses.includes('late') }"       @click="toggleStatus('late')">Late</div>
                  <div class="c1-fp-opt s1-chip-absent"   :class="{ on: filterStatuses.includes('absent') }"     @click="toggleStatus('absent')">Absent</div>
                  <div class="c1-fp-opt s1-chip-early"    :class="{ on: filterStatuses.includes('early_bird') }" @click="toggleStatus('early_bird')">Early Bird</div>
                  <div class="c1-fp-opt s1-chip-none"     :class="{ on: filterStatuses.includes('not_on_shift') }" @click="toggleStatus('not_on_shift')">Not on shift</div>
                </div>
              </div>

              <!-- Shift type -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Shift type</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: filterShiftType === '' }" @click="filterShiftType = ''">All</div>
                  <div class="c1-fp-opt s1-b-nit" :class="{ on: filterShiftType === 'night' }"   @click="filterShiftType = 'night'">Night 23–09</div>
                  <div class="c1-fp-opt s1-b-mor" :class="{ on: filterShiftType === 'morning' }" @click="filterShiftType = 'morning'">Morning 09–17</div>
                  <div class="c1-fp-opt s1-b-day" :class="{ on: filterShiftType === 'day' }"     @click="filterShiftType = 'day'">Day 11–19</div>
                  <div class="c1-fp-opt s1-b-eve" :class="{ on: filterShiftType === 'evening' }" @click="filterShiftType = 'evening'">Evening 15–23</div>
                  <div class="c1-fp-opt s1-b-vac" :class="{ on: filterShiftType === 'vacation' }" @click="filterShiftType = 'vacation'">Vacation</div>
                </div>
              </div>

              <!-- Agent search -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Search agent</div>
                <div class="c1-fp-search">
                  <svg class="c1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input class="c1-fp-sinput" v-model.trim="filterSearch" placeholder="Name…" type="text" />
                  <div v-if="filterSearch" class="c1-fp-sclear" @click="filterSearch = ''">
                    <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </div>
                </div>
              </div>

            </template>

            <!-- ═══ CHARTS CONTENT ═══ -->
            <template v-if="s1PanelActiveContent === 'charts'">

              <!-- KPI row -->
              <div class="c1-co-lbl">Shift coverage this week</div>
              <div class="c1-kpi-row">
                <div class="c1-kpi">
                  <div class="c1-kpi-lbl">Night</div>
                  <div class="c1-kpi-val s1-c-nit">{{ sumNight || 0 }}</div>
                </div>
                <div class="c1-kpi">
                  <div class="c1-kpi-lbl">Morning</div>
                  <div class="c1-kpi-val s1-c-mor">{{ sumMorning || 0 }}</div>
                </div>
                <div class="c1-kpi">
                  <div class="c1-kpi-lbl">Day</div>
                  <div class="c1-kpi-val s1-c-day">{{ sumDay || 0 }}</div>
                </div>
                <div class="c1-kpi">
                  <div class="c1-kpi-lbl">Evening</div>
                  <div class="c1-kpi-val s1-c-eve">{{ sumEvening || 0 }}</div>
                </div>
                <div class="c1-kpi">
                  <div class="c1-kpi-lbl">Total agents</div>
                  <div class="c1-kpi-val">{{ rawShifts.length }}</div>
                </div>
              </div>

              <!-- Punctuality rate per agent -->
              <div class="c1-co-lbl" style="margin-top:16px">Punctuality rate per agent</div>
              <div class="s1-punct-agent-bars">
                <div v-for="(a, i) in punctualityPerAgent" :key="i" class="s1-pab-row">
                  <div class="s1-pab-av" :style="{ background: userColor(a.name) }">{{ initials(a.name) }}</div>
                  <div class="s1-pab-name">{{ shortName(a.name) }}</div>
                  <div class="s1-pab-track">
                    <div class="s1-pab-fill" :style="{ width: a.pct + '%', background: punctColor(a.pct) }"></div>
                  </div>
                  <div class="s1-pab-val" :style="{ color: punctColor(a.pct) }">{{ a.pct }}%</div>
                </div>
                <div v-if="!punctualityPerAgent.length" class="s1-pab-empty">No punctuality data yet</div>
              </div>

              <!-- Hours worked per agent this week -->
              <div class="c1-co-lbl" style="margin-top:16px">Hours worked this week</div>
              <div class="s1-hours-bars">
                <div v-for="(a, i) in hoursPerAgent" :key="i" class="s1-hb-row">
                  <div class="s1-hb-av" :style="{ background: userColor(a.name) }">{{ initials(a.name) }}</div>
                  <div class="s1-hb-name">{{ shortName(a.name) }}</div>
                  <div class="s1-hb-track">
                    <div class="s1-hb-fill" :style="{ width: hourBarPct(a.hours) + '%' }"></div>
                  </div>
                  <div class="s1-hb-val">{{ a.hours }}h</div>
                </div>
                <div v-if="!hoursPerAgent.length" class="s1-pab-empty">No hours data yet</div>
              </div>

              <!-- Shift coverage by hour (who was on each hour) -->
              <div class="c1-co-lbl" style="margin-top:16px">Shift coverage graph (today)</div>
              <div class="s1-coverage-grid">
                <div v-for="h in coverageHours" :key="h.hour" class="s1-cov-col">
                  <div class="s1-cov-agents">
                    <div
                      v-for="(ag, ai) in h.agents" :key="ai"
                      class="s1-cov-dot"
                      :style="{ background: userColor(ag) }"
                      :title="ag"
                    ></div>
                  </div>
                  <div class="s1-cov-hour">{{ h.label }}</div>
                </div>
              </div>

              <!-- Shift type distribution bar -->
              <div class="c1-co-lbl" style="margin-top:16px">Shift type distribution</div>
              <div class="c1-vbar-chart">
                <div class="c1-vbar-row">
                  <div class="c1-vbar-lbl">Night</div>
                  <div class="c1-vbar-track">
                    <div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumNight) + '%', background: '#7c3aed' }">
                      <span class="c1-vbar-val">{{ sumNight }}</span>
                    </div>
                  </div>
                </div>
                <div class="c1-vbar-row">
                  <div class="c1-vbar-lbl">Morning</div>
                  <div class="c1-vbar-track">
                    <div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumMorning) + '%', background: '#2563eb' }">
                      <span class="c1-vbar-val">{{ sumMorning }}</span>
                    </div>
                  </div>
                </div>
                <div class="c1-vbar-row">
                  <div class="c1-vbar-lbl">Day</div>
                  <div class="c1-vbar-track">
                    <div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumDay) + '%', background: '#16a34a' }">
                      <span class="c1-vbar-val">{{ sumDay }}</span>
                    </div>
                  </div>
                </div>
                <div class="c1-vbar-row">
                  <div class="c1-vbar-lbl">Evening</div>
                  <div class="c1-vbar-track">
                    <div class="c1-vbar-fill" :style="{ width: shiftBarPct(sumEvening) + '%', background: '#ea580c' }">
                      <span class="c1-vbar-val">{{ sumEvening }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approval donut -->
              <div class="c1-co-lbl" style="margin-top:16px">Approval status</div>
              <div class="c1-don-wrap-lg">
                <div class="c1-don-lg">
                  <svg viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#1e1e2e" stroke-width="4"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#22c55e" stroke-width="4"
                      :stroke-dasharray="donArc(approvedCount, totalShiftCells) + ' ' + (88 - donArc(approvedCount, totalShiftCells))"
                      stroke-linecap="round" transform="rotate(-90 18 18)"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#6366f1" stroke-width="4"
                      :stroke-dasharray="donArc(pendingCount, totalShiftCells) + ' ' + (88 - donArc(pendingCount, totalShiftCells))"
                      :stroke-dashoffset="'-' + donArc(approvedCount, totalShiftCells)"
                      stroke-linecap="round" transform="rotate(-90 18 18)"/>
                  </svg>
                  <div class="c1-don-c-lg">{{ totalShiftCells }}</div>
                </div>
                <div class="c1-don-leg-lg">
                  <div class="c1-don-i-lg"><div class="c1-don-d-lg" style="background:#22c55e"></div>Approved <span class="c1-don-v-lg">{{ approvedCount }}</span></div>
                  <div class="c1-don-i-lg"><div class="c1-don-d-lg" style="background:#6366f1"></div>Pending <span class="c1-don-v-lg">{{ pendingCount }}</span></div>
                </div>
              </div>

            </template>

            <!-- ═══ CALENDAR CONTENT ═══ -->
            <template v-if="s1PanelActiveContent === 'calendar'">

              <!-- Color legend -->
              <div class="s1-cal-legend">
                <span class="s1-cal-leg-item s1-b-nit">Night 23–09</span>
                <span class="s1-cal-leg-item s1-b-mor">Morning 09–17</span>
                <span class="s1-cal-leg-item s1-b-day">Day 11–19</span>
                <span class="s1-cal-leg-item s1-b-eve">Evening 15–23</span>
                <span class="s1-cal-leg-item s1-b-vac">Vacation</span>
              </div>

              <div class="c1-co-lbl" style="margin-top:4px">{{ selectedTab === 'shiftsNextWeek' ? 'Next' : 'This' }} week — click any cell to see details</div>

              <!-- Week grid -->
              <div class="s1-cal-grid">
                <div class="s1-cal-head-row">
                  <div class="s1-cal-head-cell s1-cal-row-label"></div>
                  <div class="s1-cal-head-cell" v-for="day in days" :key="day.id">
                    <div class="s1-cal-day-name">{{ day.name }}</div>
                    <div class="s1-cal-day-date">{{ getDateLabel(day.id) }}</div>
                  </div>
                </div>
                <div v-if="loading" class="s1-cal-loading">
                  <div class="s1-spinner"></div> Loading…
                </div>
                <template v-else>
                  <div v-for="(userShifts, ui) in rawShifts" :key="ui" class="s1-cal-user-row">
                    <div class="s1-cal-row-label">
                      <div class="s1-cal-row-av" :style="{ background: userColor(userName(userShifts)) }">{{ initials(userName(userShifts)) }}</div>
                      <span class="s1-cal-row-name">{{ shortName(userName(userShifts)) }}</span>
                    </div>
                    <div
                      v-for="day in days" :key="day.id"
                      class="s1-cal-cell"
                      :class="getDayShiftClass(userShifts, day.id)"
                      @click="openCalDayDetail(userShifts, day.id)"
                    >
                      <span class="s1-cal-cell-label">{{ getDayShiftLabel(userShifts, day.id) }}</span>
                      <span v-if="isEmptySlot(userShifts, day.id) && !isManager" class="s1-cal-cell-claim" @click.stop="claimSlot(userShifts, day.id)">
                        <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      </span>
                    </div>
                  </div>
                  <div v-if="!rawShifts.length" class="s1-cal-loading">No shifts scheduled yet.</div>
                </template>
              </div>

              <!-- Calendar day detail slide -->
              <transition name="s1-slide-down">
                <div v-if="calDayDetail" class="s1-cal-day-detail">
                  <div class="s1-cdd-hdr">
                    <div class="s1-cdd-title">{{ dayName(calDayDetail.dayId) }} {{ getDateLabel(calDayDetail.dayId) }}</div>
                    <button class="s1-cdd-close" @click="calDayDetail = null">
                      <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <div class="s1-cdd-body">
                    <div class="s1-cdd-agent-row">
                      <div class="s1-cdd-av" :style="{ background: userColor(userName(calDayDetail.userShifts)) }">
                        {{ initials(userName(calDayDetail.userShifts)) }}
                      </div>
                      <div class="s1-cdd-info">
                        <div class="s1-cdd-name">{{ userName(calDayDetail.userShifts) }}</div>
                        <div class="s1-cdd-role">{{ userRole(calDayDetail.userShifts) }}</div>
                      </div>
                      <span class="s1-status-chip" :class="getDayShiftClass(calDayDetail.userShifts, calDayDetail.dayId)">
                        {{ getDayShiftLabel(calDayDetail.userShifts, calDayDetail.dayId) }}
                      </span>
                    </div>
                    <div class="s1-cdd-shift-time">
                      {{ getShiftTimeForDay(calDayDetail.userShifts, calDayDetail.dayId) }}
                    </div>
                    <div class="s1-cdd-actions" v-if="!isManager">
                      <button class="s1-sp-btn s1-sp-btn-switch" @click="openSwitchRequest(calDayDetail.userShifts)">
                        <svg viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                        Request switch
                      </button>
                    </div>
                  </div>
                </div>
              </transition>

            </template>


          </div>
        </div>

      </div>
    </div>

    <!-- ══════════════════════ WEEK SELECTOR SUB-HEADER ═══════════ -->
    <div class="s1-week-bar">
      <!-- Mobile accordion toggle -->
      <div class="s1-wb-toggle" @click.stop="s1WeekBarOpen = !s1WeekBarOpen">
        <span class="s1-wb-cur">{{ selectedTab === 'shiftsThisWeek' ? 'This week' : 'Next week' }}</span>
        <svg class="s1-wb-arr" :class="{ open: s1WeekBarOpen }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <!-- Items: horizontal on desktop, vertical accordion on mobile -->
      <div class="s1-wb-items" :class="{ open: s1WeekBarOpen }">
        <div class="s1-week-tabs">
          <div v-for="tab in tabs" :key="tab.alias" class="s1-week-tab" :class="{ on: selectedTab === tab.alias }" @click="selectTab(tab.alias); s1WeekBarOpen = false">{{ tab.name }}</div>
        </div>
        <div class="c1-hp"></div>
        <div class="s1-week-action" @click.stop="copyLastWeek(); s1WeekBarOpen = false" title="Copy last week's schedule to this week">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
          Copy week
        </div>
        <div v-if="isManager" class="s1-week-action s1-week-action-approve" @click.stop="approveAllShifts(); s1WeekBarOpen = false" title="Approve all shifts">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><polyline points="20 6 9 17 4 12"/></svg>
          Approve all
        </div>
      </div>
    </div>

    <!-- spacer: compensates for fixed s1-week-bar (38px) so content clears below it -->
    <div class="s1-wk-spacer"></div>

    <!-- ══════════════════════ ATTENDANCE SUMMARY BAR ═══════════ -->
    <div class="s1-attendance-bar" v-if="!loading && rawShifts.length">
      <div class="s1-att-pill s1-att-ontime">
        <span class="s1-att-dot" style="background:#22c55e"></span>
        On Time: <strong>{{ s1AttendanceSummary.onTime }}</strong>
      </div>
      <div class="s1-att-pill s1-att-late">
        <span class="s1-att-dot" style="background:#f59e0b"></span>
        Late: <strong>{{ s1AttendanceSummary.late }}</strong>
      </div>
      <div class="s1-att-pill s1-att-absent">
        <span class="s1-att-dot" style="background:#ef4444"></span>
        Absent: <strong>{{ s1AttendanceSummary.absent }}</strong>
      </div>
      <div class="s1-att-pill s1-att-early">
        <span class="s1-att-dot" style="background:#3b82f6"></span>
        Early Bird: <strong>{{ s1AttendanceSummary.earlyBird }}</strong>
      </div>
      <div class="c1-hp"></div>
      <div class="s1-att-export" @click="s1ExportCSV" title="Export this week's shifts as CSV">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        Export CSV
      </div>
    </div>

    <!-- ══════════════════════ WHO IS ON NOW strip ═══════════ -->
    <div class="s1-on-now-strip">
      <div class="s1-on-now-label">On shift now</div>
      <div class="s1-on-now-scroll">
        <template v-if="onNowAgents.length">
          <div v-for="(agent, i) in onNowAgents" :key="i" class="s1-on-now-chip" :class="{ active: agent.onShift }">
            <div class="s1-on-now-av" :style="{ background: agent.onShift ? userColor(agent.name) : '#2a2a3a' }">
              {{ initials(agent.name) }}
            </div>
            <span class="s1-on-now-name">{{ shortName(agent.name) }}</span>
            <span v-if="agent.onShift" class="s1-on-now-dot"></span>
          </div>
        </template>
        <div v-else class="s1-on-now-empty">No agents on shift yet</div>
      </div>
    </div>

    <!-- ══════════════════════ TEAM VIEW TOGGLE ═══════════ -->
    <div class="sh1-team-toggle-bar">
      <button
        class="sh1-tv-btn"
        :class="{ on: sh1TeamView === null }"
        @click="sh1TeamView = null"
      >All Agents</button>
      <button
        class="sh1-tv-btn sh1-tv-t1"
        :class="{ on: sh1TeamView === 1 }"
        @click="sh1TeamView = 1"
      >⚡ Team 1</button>
      <button
        class="sh1-tv-btn sh1-tv-t2"
        :class="{ on: sh1TeamView === 2 }"
        @click="sh1TeamView = 2"
      >🔥 Team 2</button>
    </div>

    <!-- Team summary bar (only when viewing All) -->
    <div v-if="sh1TeamView === null && !loading && rawShifts.length" class="sh1-team-summary-bar">
      <div class="sh1-tsb-item sh1-tsb-t1">
        <span class="sh1-tsb-dot"></span>
        <span>Team 1: <strong>{{ sh1Team1Count }}</strong> agents on shift</span>
      </div>
      <div class="sh1-tsb-sep">|</div>
      <div class="sh1-tsb-item sh1-tsb-t2">
        <span class="sh1-tsb-dot"></span>
        <span>Team 2: <strong>{{ sh1Team2Count }}</strong> agents on shift</span>
      </div>
    </div>

    <!-- ══════════════════════ MAIN CONTENT ════════════════ -->
    <div class="s1-content">

      <!-- Loading -->
      <div v-if="loading" class="s1-loading">
        <div class="s1-spinner"></div>
        <span>Loading shifts…</span>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredShifts.length === 0" class="s1-empty">
        <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <p>No shifts scheduled yet</p>
        <div v-if="isManager" class="s1-empty-cta" @click="openSchedulePanel(null)">
          <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Schedule first shift
        </div>
      </div>

      <!-- Accordion -->
      <div v-else class="s1-accordion">

        <!-- Column headers -->
        <div class="s1-acc-header-row">
          <div class="s1-acc-col-agent">Agent</div>
          <div class="s1-acc-col-shift">Shift</div>
          <div class="s1-acc-col-status">Status</div>
          <div class="s1-acc-col-clock">Clock in</div>
          <div class="s1-acc-col-clock">Clock out</div>
          <div class="s1-acc-col-dur">Hours</div>
          <div class="s1-acc-col-actions"></div>
        </div>

        <div
          v-for="(userShifts, idx) in filteredShifts"
          :key="idx"
          class="s1-acc-row"
          :class="{
            's1-acc-expanded': expandedRow === idx,
            's1-acc-all-ok': userShifts.every(s => s.approved === 1),
            'sh1-team1-row': userTeamId(userShifts) === 1,
            'sh1-team2-row': userTeamId(userShifts) === 2
          }"
        >
          <!-- Collapsed row -->
          <div class="s1-acc-collapsed" @click="toggleRow(idx)">
            <div class="s1-acc-col-agent">
              <div class="s1-av" :style="{ background: userColor(userName(userShifts)) }">
                {{ initials(userName(userShifts)) }}
                <div v-if="isOnShiftNow(userShifts)" class="s1-av-pulse"></div>
              </div>
              <div class="s1-acc-name-block">
                <span class="s1-acc-name">{{ userName(userShifts) }}</span>
                <span v-if="isManager" class="s1-acc-team">{{ userRole(userShifts) }}</span>
              </div>
            </div>
            <div class="s1-acc-col-shift">
              <template v-if="todayShift(userShifts)">
                <span class="s1-shift-badge" :class="shiftBadgeClass(todayShift(userShifts))">{{ shiftLabel(todayShift(userShifts)) }}</span>
                <span class="s1-shift-time">{{ shiftTime(todayShift(userShifts)) }}</span>
              </template>
              <span v-else class="s1-muted">—</span>
            </div>
            <div class="s1-acc-col-status">
              <span v-if="todayShift(userShifts)" class="s1-status-chip" :class="statusChipClass(todayShift(userShifts))">
                {{ statusLabel(todayShift(userShifts)) }}
              </span>
              <span v-else class="s1-status-chip s1-status-none">—</span>
            </div>
            <div class="s1-acc-col-clock">
              <span class="s1-clock-val">{{ (todayShift(userShifts) && todayShift(userShifts).clock_in) || '—' }}</span>
            </div>
            <div class="s1-acc-col-clock">
              <span class="s1-clock-val">{{ (todayShift(userShifts) && todayShift(userShifts).clock_out) || '—' }}</span>
            </div>
            <div class="s1-acc-col-dur">
              <span class="s1-dur">{{ calcHours(todayShift(userShifts)) }}</span>
            </div>
            <div class="s1-acc-col-actions">
              <div class="s1-row-btn s1-btn-detail" @click.stop="openAgentDetail(userShifts)" title="Agent detail">
                <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div v-if="isManager" class="s1-row-btn s1-btn-sched" @click.stop="openSchedulePanel(userShifts)" title="Schedule shift">
                <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </div>
              <div v-if="!isManager" class="s1-row-btn s1-btn-switch" @click.stop="openSwitchRequest(userShifts)" title="Request switch">
                <svg viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
              </div>
              <div v-if="isManager && userShifts.some(s => s.approved !== 1 && s.choice_id !== 4)" class="s1-row-btn s1-btn-approve" @click.stop="approveUser(userId(userShifts), false)" title="Approve all">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div class="s1-acc-chev" :class="{ open: expandedRow === idx }">
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
          </div>

          <!-- Expanded detail -->
          <div v-show="expandedRow === idx" class="s1-acc-detail">

            <!-- Shift history cards -->
            <div class="s1-detail-grid">
              <div v-for="(s, si) in userShifts" :key="si" class="s1-detail-shift-card" :class="shiftBadgeClass(s)">
                <div class="s1-dsc-top">
                  <span class="s1-dsc-day">{{ dayName(s.day_number) }}</span>
                  <span class="s1-dsc-badge" :class="shiftBadgeClass(s)">{{ shiftLabel(s) }}</span>
                  <span v-if="s.approved === 1" class="s1-dsc-approved">
                    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    Approved
                  </span>
                  <span v-else class="s1-dsc-pending">Pending</span>
                </div>
                <div class="s1-dsc-rows">
                  <div class="s1-dsc-row"><span class="s1-dsc-lbl">Shift</span><span class="s1-dsc-val">{{ shiftTime(s) }}</span></div>
                  <div class="s1-dsc-row"><span class="s1-dsc-lbl">Clock-in</span><span class="s1-dsc-val">{{ s.clock_in || '—' }}</span></div>
                  <div class="s1-dsc-row"><span class="s1-dsc-lbl">Clock-out</span><span class="s1-dsc-val">{{ s.clock_out || '—' }}</span></div>
                  <div class="s1-dsc-row"><span class="s1-dsc-lbl">Hours</span><span class="s1-dsc-val">{{ calcHours(s) }}</span></div>
                  <div class="s1-dsc-row"><span class="s1-dsc-lbl">Break</span><span class="s1-dsc-val">{{ s.break_duration || '—' }}</span></div>
                  <div class="s1-dsc-row">
                    <span class="s1-dsc-lbl">Punctuality</span>
                    <span class="s1-dsc-val" :style="{ color: punctColor(s.punctuality_score) }">
                      {{ s.punctuality_score !== undefined ? s.punctuality_score + '%' : '—' }}
                    </span>
                  </div>
                  <div v-if="s.notes" class="s1-dsc-row"><span class="s1-dsc-lbl">Notes</span><span class="s1-dsc-val">{{ s.notes }}</span></div>
                </div>
                <div v-if="isManager" class="s1-dsc-actions">
                  <div class="s1-dd-wrap" v-click-outside="() => closeDropdown()">
                    <button class="s1-dsc-btn s1-dsc-btn-change" @click.stop="openDropdown(s.day_number, s.choice_id, si, idx)">Change</button>
                    <Shifts1Dropdown
                      v-if="dropdownOpen && dropdownDay === s.day_number && dropdownUserIdx === idx && dropdownShiftI === si"
                      :shift="{ id: s.id, choice_id: s.choice_id, user_id: userId(userShifts) }"
                      :shift-status="s.approved === 1 ? 'shift-approved' : undefined"
                      :is-friday="s.day_number === 6"
                      :user-strength="userStrength"
                      @closeDropdown="onDropdownClose"
                    />
                  </div>
                  <button v-if="s.approved !== 1 && s.choice_id !== 4" class="s1-dsc-btn s1-dsc-btn-approve" @click="approveSingleShift(s.id)">Approve</button>
                  <button v-if="s.approved === 1" class="s1-dsc-btn s1-dsc-btn-unapprove" @click="unapproveShift(s.id)">Unapprove</button>
                </div>
              </div>
            </div>

            <!-- Weekly summary bar + totals -->
            <div class="s1-week-summary">
              <div class="s1-ws-bar">
                <div v-for="day in days" :key="day.id" class="s1-wb-cell">
                  <div class="s1-wb-day">{{ day.name }}</div>
                  <div class="s1-wb-slot" :class="getDayShiftClass(userShifts, day.id)">
                    {{ getDayShiftLabel(userShifts, day.id) }}
                  </div>
                </div>
              </div>
              <div class="s1-ws-totals">
                <div class="s1-wst-item">
                  <div class="s1-wst-lbl">This week</div>
                  <div class="s1-wst-val">{{ weekHours(userShifts) }}h</div>
                </div>
                <div class="s1-wst-item">
                  <div class="s1-wst-lbl">Shifts</div>
                  <div class="s1-wst-val">{{ userShifts.filter(s => s.choice_id !== 4 && s.choice_id !== 6).length }}</div>
                </div>
                <div class="s1-wst-item">
                  <div class="s1-wst-lbl">Approved</div>
                  <div class="s1-wst-val s1-c-ok">{{ userShifts.filter(s => s.approved === 1).length }}/{{ userShifts.length }}</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- ════════════ SLIDE RIGHT — Agent shift detail ════════ -->
    <transition name="s1-slide-right">
      <div v-if="agentDetailOpen" class="s1-slide-panel s1-slide-panel-right" v-click-outside="closeAgentDetail">
        <div class="s1-sp-header">
          <div v-if="agentDetailData" class="s1-sp-av" :style="{ background: userColor(userName(agentDetailData)) }">
            {{ initials(userName(agentDetailData)) }}
          </div>
          <div class="s1-sp-title-block">
            <div class="s1-sp-title">{{ agentDetailData ? userName(agentDetailData) : 'Agent' }}</div>
            <div class="s1-sp-sub">Shift history &amp; stats</div>
          </div>
          <div class="c1-hp"></div>
          <button class="s1-sp-close" @click="closeAgentDetail">
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="s1-sp-body">

          <!-- This week's schedule mini-calendar -->
          <div class="s1-sp-section-lbl">Schedule this week</div>
          <div class="s1-sp-week-grid" v-if="agentDetailData">
            <div v-for="day in days" :key="day.id" class="s1-sp-wg-cell">
              <div class="s1-sp-wg-day">{{ day.name }}</div>
              <div class="s1-sp-wg-badge" :class="getDayShiftClass(agentDetailData, day.id)">
                {{ getDayShiftLabel(agentDetailData, day.id) }}
              </div>
            </div>
          </div>

          <!-- Totals this week/month -->
          <div class="s1-sp-section-lbl" style="margin-top:16px">Hours summary</div>
          <div class="s1-sp-stats-row">
            <div class="s1-sp-stat">
              <div class="s1-sp-stat-val">{{ agentDetailData ? weekHours(agentDetailData) : 0 }}h</div>
              <div class="s1-sp-stat-lbl">This week</div>
            </div>
            <div class="s1-sp-stat">
              <div class="s1-sp-stat-val">{{ agentMonthHours }}h</div>
              <div class="s1-sp-stat-lbl">This month</div>
            </div>
            <div class="s1-sp-stat">
              <div class="s1-sp-stat-val">{{ agentPunctScore }}%</div>
              <div class="s1-sp-stat-lbl">Punctuality</div>
            </div>
          </div>

          <!-- Punctuality history -->
          <div class="s1-sp-section-lbl" style="margin-top:16px">Punctuality history</div>
          <div class="s1-sp-punct">
            <div v-for="(w, i) in agentPunctHistory" :key="i" class="s1-sp-punct-row">
              <span class="s1-sp-punct-week">{{ w.label }}</span>
              <div class="s1-sp-punct-track">
                <div class="s1-sp-punct-fill" :style="{ width: w.pct + '%', background: punctColor(w.pct) }"></div>
              </div>
              <span class="s1-sp-punct-val" :style="{ color: punctColor(w.pct) }">{{ w.pct }}%</span>
            </div>
          </div>

          <!-- Manager actions -->
          <template v-if="isManager && agentDetailData">
            <div class="s1-sp-section-lbl" style="margin-top:18px">Actions</div>
            <div class="s1-sp-actions">
              <button class="s1-sp-btn s1-sp-btn-approve" v-if="agentDetailData.some(s => s.approved !== 1)" @click="approveUser(userId(agentDetailData), false)">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                Approve all shifts
              </button>
              <button class="s1-sp-btn s1-sp-btn-reject" @click="approveUser(userId(agentDetailData), true)">
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                Unapprove all
              </button>
              <button class="s1-sp-btn s1-sp-btn-switch" @click="openSchedulePanel(agentDetailData); closeAgentDetail()">
                <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                Schedule shift
              </button>
            </div>
          </template>

          <!-- Agent switch request -->
          <template v-if="!isManager && agentDetailData">
            <div class="s1-sp-section-lbl" style="margin-top:18px">Switch request</div>
            <div class="s1-sp-actions">
              <button class="s1-sp-btn s1-sp-btn-switch" @click="openSwitchRequest(agentDetailData); closeAgentDetail()">
                <svg viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                Request shift switch
              </button>
            </div>
          </template>

        </div>
      </div>
    </transition>

    <!-- ════════════ SLIDE LEFT — Schedule shift ════════════ -->
    <transition name="s1-slide-left">
      <div v-if="scheduleOpen" class="s1-slide-panel s1-slide-panel-left" v-click-outside="closeSchedule">
        <div class="s1-sp-header">
          <svg viewBox="0 0 24 24" class="s1-sp-hico"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <div class="s1-sp-title-block">
            <div class="s1-sp-title">Schedule shift</div>
            <div class="s1-sp-sub">{{ scheduleTarget ? 'Assign to ' + userName(scheduleTarget) : 'Assign to agent' }}</div>
          </div>
          <div class="c1-hp"></div>
          <button class="s1-sp-close" @click="closeSchedule">
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="s1-sp-body">
          <div class="s1-sp-section-lbl">Shift type</div>
          <div class="s1-sch-types">
            <div
              v-for="t in shiftTypes" :key="t.id"
              class="s1-sch-type-chip"
              :class="['s1-b-' + t.cls, { on: scheduleForm.choiceId === t.id }]"
              @click="scheduleForm.choiceId = t.id"
            >
              <span class="s1-sch-type-badge">{{ t.short }}</span>
              <span class="s1-sch-type-name">{{ t.name }}</span>
              <span class="s1-sch-type-time">{{ t.time }}</span>
            </div>
          </div>

          <div class="s1-sp-section-lbl" style="margin-top:14px">Day</div>
          <div class="s1-sch-days">
            <div
              v-for="day in days" :key="day.id"
              class="s1-sch-day-chip"
              :class="{ on: scheduleForm.dayNumber === day.id }"
              @click="scheduleForm.dayNumber = day.id"
            >
              <div class="s1-sch-day-name">{{ day.name }}</div>
              <div class="s1-sch-day-date">{{ getDateLabel(day.id) }}</div>
            </div>
          </div>

          <div class="s1-sp-section-lbl" style="margin-top:14px">Recurring</div>
          <div class="s1-sch-recurring">
            <div class="s1-sch-tog" :class="{ on: scheduleForm.recurring }" @click="scheduleForm.recurring = !scheduleForm.recurring">
              <div class="s1-sch-tog-dot"></div>
            </div>
            <span>Repeat every week</span>
          </div>

          <div class="s1-sp-section-lbl" style="margin-top:14px">Notes</div>
          <textarea class="s1-sch-notes" v-model="scheduleForm.notes" placeholder="Optional notes…" rows="3"></textarea>

          <button
            class="s1-sp-btn s1-sp-btn-approve s1-sch-submit"
            :disabled="!scheduleForm.choiceId || !scheduleForm.dayNumber"
            @click="submitSchedule"
          >
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
            Assign shift
          </button>
        </div>
      </div>
    </transition>

    <!-- ════════════ SLIDE RIGHT — Switch request ════════════ -->
    <transition name="s1-slide-right">
      <div v-if="switchRequestOpen" class="s1-slide-panel s1-slide-panel-right s1-switch-panel" v-click-outside="closeSwitchRequest">
        <div class="s1-sp-header">
          <svg viewBox="0 0 24 24" class="s1-sp-hico"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
          <div class="s1-sp-title-block">
            <div class="s1-sp-title">Shift switch request</div>
            <div class="s1-sp-sub">Request to switch with {{ switchTarget ? userName(switchTarget) : 'agent' }}</div>
          </div>
          <div class="c1-hp"></div>
          <button class="s1-sp-close" @click="closeSwitchRequest">
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="s1-sp-body">
          <div class="s1-sp-section-lbl">Switch scope</div>
          <div class="s1-sch-types">
            <div class="s1-sch-type-chip" :class="{ on: switchForm.scope === 'day' }" @click="switchForm.scope = 'day'">
              <span class="s1-sch-type-name">This day only</span>
              <span class="s1-sch-type-time">Single swap</span>
            </div>
            <div class="s1-sch-type-chip" :class="{ on: switchForm.scope === 'all' }" @click="switchForm.scope = 'all'">
              <span class="s1-sch-type-name">All shifts together</span>
              <span class="s1-sch-type-time">All overlapping shifts</span>
            </div>
          </div>

          <div class="s1-sp-section-lbl" style="margin-top:14px">Select day</div>
          <div class="s1-sch-days">
            <div
              v-for="day in days" :key="day.id"
              class="s1-sch-day-chip"
              :class="{ on: switchForm.dayNumber === day.id }"
              @click="switchForm.dayNumber = day.id"
            >
              <div class="s1-sch-day-name">{{ day.name }}</div>
              <div class="s1-sch-day-date">{{ getDateLabel(day.id) }}</div>
            </div>
          </div>

          <div class="s1-sp-section-lbl" style="margin-top:14px">Message (optional)</div>
          <textarea class="s1-sch-notes" v-model="switchForm.message" placeholder="Why do you want to switch?" rows="3"></textarea>

          <button
            class="s1-sp-btn s1-sp-btn-approve s1-sch-submit"
            :disabled="!switchForm.scope || !switchForm.dayNumber"
            @click="submitSwitchRequest"
          >
            <svg viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/></svg>
            Send switch request
          </button>
        </div>
      </div>
    </transition>

    <!-- Overlay backdrop -->
    <div v-if="agentDetailOpen || scheduleOpen || switchRequestOpen" class="s1-overlay" @click="closeAgentDetail(); closeSchedule(); closeSwitchRequest()"></div>

  </div>
</template>

<script>
import VikiFace from "../../clients1.feature/VikiFace.vue";
import moment from "moment";
import apiClientCreator from "@/helpers/api/request";
import { mapState } from "vuex";
import vClickOutside from "v-click-outside";
import Shifts1Dropdown from "../components/Shifts1Dropdown";
import C1ModCalendar from '../../../components/C1ModCalendar.vue';

const API = "/api";
const COLORS = ["#7C5CFC","#E8891C","#2D9D5E","#C75450","#E05A3A","#7A7067","#5B8DD9","#B8960F","#9B59B6","#1ABC9C"];

// Shift definitions
const SHIFT_DEFS = {
  night:   { id: 'night',   choice_id: 5, name: 'Night',   short: 'NIT', cls: 'nit', time: '23:00–09:00', startH: 23, endH: 9  },
  morning: { id: 'morning', choice_id: 1, name: 'Morning', short: 'MOR', cls: 'mor', time: '09:00–17:00', startH: 9,  endH: 17 },
  day:     { id: 'day',     choice_id: 2, name: 'Day',     short: 'DAY', cls: 'day', time: '11:00–19:00', startH: 11, endH: 19 },
  evening: { id: 'evening', choice_id: 3, name: 'Evening', short: 'EVE', cls: 'eve', time: '15:00–23:00', startH: 15, endH: 23 },
};

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('user-token') };
}

export default {
  name: "Shifts1Container",
  components: { Shifts1Dropdown, VikiFace, C1ModCalendar },
  directives: { clickOutside: vClickOutside.directive },

  watch: {
    panel(val) {
      if (val && val !== 'nav' && val !== 'menu') {
        this.s1PanelActiveContent = val;
      }
    },
    filterRole() { this.expandedRow = null; },
    filterSearch() { this.expandedRow = null; },
    filterStatuses() { this.expandedRow = null; },
    filterShiftType() { this.expandedRow = null; },
  },

  computed: {
    c1CurrentPageLabel() {
      const map = {
        '/clients1':'Clients', '/tasks1':'Tasks', '/stats1':'Statistics',
        '/chat1':'Chat', '/communications1':'Communications', '/affiliation1':'Affiliation',
        '/finance1':'Finance', '/shifts1':'Shifts', '/games1':'Games',
        '/users1':'Users', '/brokers1':'Brokers', '/security1':'Security',
        '/quiz1':'Quiz', '/instruction1':'Instructions', '/settings1':'Settings', '/profile1':'My Profile',
      };
      return map[this.$route.path] || 'Menu';
    },
    c1UserInitials() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "?";
      return ((u.first_name || "")[0] || "").toUpperCase() + ((u.last_name || "")[0] || "").toUpperCase();
    },
    c1UserFullName() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "User";
      return [u.first_name, u.last_name].filter(Boolean).join(" ") || "User";
    },
    c1RoleLabel() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "";
      const numMap = { 1: "Admin", 2: "Manager", 3: "Agent", 4: "Broker" };
      const strMap = { admin: "Admin", manager: "Manager", agent: "Agent", broker: "Broker", super_admin: "Admin" };
      return numMap[Number(u.role)] || strMap[String(u.role).toLowerCase()] || u.role || "";
    },
    c1TzUK() { return this.c1TzTime("Europe/London"); },
    c1TzIL() { return this.c1TzTime("Asia/Jerusalem"); },
    c1TzCB() { return this.c1TzTime("America/Cayman"); },

    s1PanelIsOpen() {
      return !!this.panel && this.panel !== 'menu';
    },
    s1PanelAtContent() {
      return this.s1PanelIsOpen && this.panel !== 'nav';
    },
    s1ActivePanelLabel() {
      if (!this.s1PanelIsOpen || this.panel === 'nav') return 'Panels';
      const labels = { filters: 'Filters', charts: 'Charts', calendar: 'Calendar' };
      return labels[this.s1PanelActiveContent] || 'Panels';
    },

    ...mapState("user", ["auth"]),

    isManager() {
      if (!this.auth || !this.auth.user) return false;
      const r = this.auth.user.role;
      // role can be numeric (1=admin, 2=manager) or string
      if (typeof r === "number" || (typeof r === "string" && /^\d+$/.test(r))) {
        return Number(r) === 1 || Number(r) === 2;
      }
      const role = String(r || "").toLowerCase();
      return role === "manager" || role === "admin" || role === "super_admin";
    },

    userInitials() {
      if (!this.auth || !this.auth.user) return "U";
      const { first_name, last_name } = this.auth.user;
      return ((first_name || "")[0] + (last_name || "")[0]).toUpperCase() || "U";
    },

    timeOfDay() {
      const h = new Date().getHours();
      if (h < 12) return "Morning";
      if (h < 18) return "Afternoon";
      return "Evening";
    },

    activeFilterCount() {
      let c = 0;
      if (this.filterRole) c++;
      if (this.filterStatuses.length) c++;
      if (this.filterSearch) c++;
      if (this.filterShiftType) c++;
      if (this.datePreset && this.datePreset !== 'tw') c++;
      return c || null;
    },

    filteredShifts() {
      let list = this.rawShifts;
      if (!this.isManager && this.auth && this.auth.user) {
        list = list.filter(u => {
          const uid = u[0] && u[0].user && u[0].user[0] ? u[0].user[0].id : null;
          return uid === this.auth.user.id;
        });
      }
      if (this.filterSearch) {
        const q = this.filterSearch.toLowerCase();
        list = list.filter(u => this.userName(u).toLowerCase().includes(q));
      }
      if (this.filterRole) {
        list = list.filter(u => (this.userRole(u) || "").toLowerCase() === this.filterRole);
      }
      if (this.filterShiftType) {
        const choiceMap = { night: 5, morning: 1, day: 2, evening: 3, vacation: 6 };
        const cid = choiceMap[this.filterShiftType];
        list = list.filter(u => u.some(s => s.choice_id === cid));
      }
      if (this.filterStatuses.length) {
        list = list.filter(u => {
          const ts = this.todayShift(u);
          if (!ts) return this.filterStatuses.includes('not_on_shift');
          return this.filterStatuses.includes(this.computeAttendanceStatus(ts));
        });
      }
      if (this.sh1TeamView !== null) {
        list = list.filter(u => this.userTeamId(u) === this.sh1TeamView);
      }
      return list;
    },

    sh1Team1Count() {
      return this.rawShifts.filter(u => this.userTeamId(u) === 1 && this.isOnShiftNow(u)).length;
    },

    sh1Team2Count() {
      return this.rawShifts.filter(u => this.userTeamId(u) === 2 && this.isOnShiftNow(u)).length;
    },

    totalShiftCells() {
      return this.rawShifts.reduce((a, u) => a + u.length, 0);
    },

    approvedCount() {
      return this.rawShifts.reduce((a, u) => a + u.filter(s => s.approved === 1).length, 0);
    },

    pendingCount() {
      return this.totalShiftCells - this.approvedCount;
    },

    onNowAgents() {
      const now = new Date();
      const h = now.getHours();
      const todayNum = now.getDay() === 0 ? 1 : now.getDay() + 1;
      return this.rawShifts.map(u => {
        const name = this.userName(u);
        const s = u.find(sh => sh.day_number === todayNum);
        let onShift = false;
        if (s) onShift = this.isHourInShift(h, s.choice_id);
        return { name, onShift };
      });
    },

    punctualityPerAgent() {
      return this.rawShifts.map(u => {
        const name = this.userName(u);
        const scored = u.filter(s => s.punctuality_score !== undefined && s.punctuality_score !== null);
        const pct = scored.length
          ? Math.round(scored.reduce((a, s) => a + (s.punctuality_score || 0), 0) / scored.length)
          : 0;
        return { name, pct };
      }).filter(a => a.name !== 'Unknown');
    },

    hoursPerAgent() {
      return this.rawShifts.map(u => {
        const name = this.userName(u);
        const hours = u.reduce((a, s) => a + this.rawHours(s), 0);
        return { name, hours: Math.round(hours * 10) / 10 };
      }).filter(a => a.name !== 'Unknown');
    },

    coverageHours() {
      const hours = [];
      for (let h = 0; h < 24; h++) {
        const now = new Date();
        const todayNum = now.getDay() === 0 ? 1 : now.getDay() + 1;
        const agents = this.rawShifts
          .filter(u => {
            const s = u.find(sh => sh.day_number === todayNum);
            return s && this.isHourInShift(h, s.choice_id);
          })
          .map(u => this.userName(u));
        if (agents.length || h % 3 === 0) {
          hours.push({ hour: h, label: h.toString().padStart(2, '0') + ':00', agents });
        }
      }
      return hours;
    },

    agentMonthHours() {
      if (!this.agentDetailData) return 0;
      // approximate: multiply week hours * 4
      return Math.round(this.weekHours(this.agentDetailData) * 4);
    },

    agentPunctScore() {
      if (!this.agentDetailData) return 0;
      const scored = this.agentDetailData.filter(s => s.punctuality_score !== undefined);
      if (!scored.length) return 0;
      return Math.round(scored.reduce((a, s) => a + (s.punctuality_score || 0), 0) / scored.length);
    },

    agentPunctHistory() {
      // Derived from available data; fallback to stubs if API doesn't return history
      return [
        { label: "This week", pct: this.agentPunctScore },
        { label: "Last week", pct: Math.max(0, this.agentPunctScore - 5) },
        { label: "2 weeks ago", pct: Math.max(0, this.agentPunctScore - 12) },
        { label: "3 weeks ago", pct: Math.max(0, this.agentPunctScore + 3 > 100 ? 100 : this.agentPunctScore + 3) },
      ];
    },

    vikiAlerts() {
      return [...this.lateAlerts, ...this.noShowAlerts, ...this.switchNotifications];
    },

    s1AttendanceSummary() {
      const now = new Date();
      const todayNum = now.getDay() === 0 ? 1 : now.getDay() + 1;
      let onTime = 0, late = 0, absent = 0, earlyBird = 0;
      this.rawShifts.forEach(u => {
        const s = u.find(sh => sh.day_number === todayNum);
        if (!s || s.choice_id === 4 || s.choice_id === 6 || s.choice_id === 8) return;
        const status = this.computeAttendanceStatus(s);
        if (status === 'on_time') onTime++;
        else if (status === 'late') late++;
        else if (status === 'absent') absent++;
        else if (status === 'early_bird') earlyBird++;
      });
      return { onTime, late, absent, earlyBird };
    },
  },

  data() {
    return {
      c1Status: 'available',
      c1StatusSince: 0,
      c1StatusElapsed: '00:00',
      c1StatusMenuOpen: false,
      _c1StatusInterval: null,
      c1HamMenuOpen: false,
      c1ShiftMenuOpen: false,
      c1TopTab: null,
      c1ShiftElapsed: '00:00',
      c1ShiftStartTime: 0,
      c1BathroomCount: 0,
      c1CoffeeCount: 0,
      c1WeekLog: [],
      loading: false,
      c1NavMode: 'panels',
      c1VikiMood: 'neutral',
      c1VikiSpeaking: false,
      panel: 'nav',
      s1PanelActiveContent: null,
      s1VikiOpen: false,
      s1WeekBarOpen: false,
      selectedTab: "shiftsThisWeek",
      tabs: [
        { id: 1, name: "This week", alias: "shiftsThisWeek" },
        { id: 2, name: "Next week", alias: "shiftsNextWeek" },
      ],
      days: [
        { id: 2, name: "Mon" }, { id: 3, name: "Tue" }, { id: 4, name: "Wed" },
        { id: 5, name: "Thu" }, { id: 6, name: "Fri" }, { id: 7, name: "Sat" }, { id: 1, name: "Sun" },
      ],
      rawShifts: [],
      userStrength: [],
      sumNight: 0, sumMorning: 0, sumDay: 0, sumEvening: 0,

      // Filters
      filterRole: "",
      filterStatuses: [],
      filterSearch: "",
      filterShiftType: "",
      datePreset: "tw",

      // Accordion
      expandedRow: null,

      // Dropdown
      dropdownOpen: false,
      dropdownDay: null,
      dropdownUserIdx: null,
      dropdownShiftI: null,

      // Slide panels
      agentDetailOpen: false,
      agentDetailData: null,
      scheduleOpen: false,
      scheduleTarget: null,
      scheduleForm: { choiceId: null, dayNumber: null, recurring: false, notes: "" },

      // Switch request
      switchRequestOpen: false,
      switchTarget: null,
      switchForm: { scope: 'day', dayNumber: null, message: "" },
      switchNotifications: [],

      // Calendar day detail
      calDayDetail: null,

      // Team view
      sh1TeamView: null, // null=all, 1=team1, 2=team2

      // Viki alerts
      lateAlerts: [],
      noShowAlerts: [],
      openSlots: [],

      // Morning welcome banner
      morningBanner: null,

      // Idle tracking
      idleWarning: false,
      idleCountdown: 30,
      idleTimer: null,
      idleCountdownTimer: null,
      lastActivity: Date.now(),

      // Toast
      toast: null,
      toastTimer: null,

      // Shift types
      shiftTypes: [
        { id: 5, name: "Night",    short: "NIT", cls: "nit", time: "23:00–09:00" },
        { id: 1, name: "Morning",  short: "MOR", cls: "mor", time: "09:00–17:00" },
        { id: 2, name: "Day",      short: "DAY", cls: "day", time: "11:00–19:00" },
        { id: 3, name: "Evening",  short: "EVE", cls: "eve", time: "15:00–23:00" },
        { id: 6, name: "Vacation", short: "VAC", cls: "vac", time: "Full day" },
        { id: 8, name: "Illness",  short: "ILL", cls: "ill", time: "Full day" },
      ],
    };
  },

  created() {
    const _keepNav = sessionStorage.getItem('c1keepNav'); if (_keepNav) sessionStorage.removeItem('c1keepNav');
    const _openPanel = sessionStorage.getItem('c1openPanel'); if (_openPanel) sessionStorage.removeItem('c1openPanel');
    if (_openPanel) { this.panel = 'filters'; }
    else if (_keepNav) { this.panel = 'nav'; this.c1NavMode = 'panels'; }
    else { this.panel = 'nav'; }
  },

  mounted() {
    if (!this.panel) this.panel = 'nav';
    const savedStatus = localStorage.getItem('c1_status');
    const savedSince  = localStorage.getItem('c1_status_since');
    const today = new Date().toISOString().slice(0, 10);
    const savedShiftDay = localStorage.getItem('c1_shift_day');
    if (savedShiftDay !== today) {
      this.c1ShiftStartTime = Date.now();
      this.c1BathroomCount = 0;
      this.c1CoffeeCount = 0;
      localStorage.setItem('c1_status', 'shift');
      localStorage.setItem('c1_status_since', String(Date.now()));
      localStorage.setItem('c1_shift_start', String(Date.now()));
      localStorage.setItem('c1_shift_day', today);
      localStorage.setItem('c1_bathroom_count', '0');
      localStorage.setItem('c1_coffee_count', '0');
    } else {
      if (savedStatus) this.c1Status = savedStatus;
      this.c1ShiftStartTime = parseInt(localStorage.getItem('c1_shift_start') || String(Date.now()), 10);
      this.c1BathroomCount = parseInt(localStorage.getItem('c1_bathroom_count') || '0', 10);
      this.c1CoffeeCount   = parseInt(localStorage.getItem('c1_coffee_count') || '0', 10);
    }
    this.c1StatusSince = savedSince ? parseInt(savedSince, 10) : Date.now();
    this._c1StatusInterval = setInterval(this.c1TickStatus, 1000);
    document.documentElement.style.setProperty('--gnm-sw', '0px');
    this.loadTabData();
    this.fetchTotalSumForSlots();
    this.fetchUsersStrength();
    this.startIdleTracking();
    this.checkMorningGreeting();
    this.pollVikiAlerts();
  },

  beforeDestroy() {
    if (this._c1StatusInterval) clearInterval(this._c1StatusInterval);
    this.stopIdleTracking();
    if (this._vikiPoll) clearInterval(this._vikiPoll);
  },

  methods: {
    c1SetStatus(s) {
      if (s === 'bathroom') this.c1BathroomCount++;
      if (s === 'coffee')   this.c1CoffeeCount++;
      this.c1Status = s;
      this.c1StatusSince = Date.now();
      this.c1StatusElapsed = '00:00';
      this.c1ShiftMenuOpen = false;
      localStorage.setItem('c1_status', s);
      localStorage.setItem('c1_status_since', String(this.c1StatusSince));
      if (s === 'bathroom') localStorage.setItem('c1_bathroom_count', String(this.c1BathroomCount));
      if (s === 'coffee')   localStorage.setItem('c1_coffee_count',   String(this.c1CoffeeCount));
      if (s === 'end') this.c1SaveTodayToLog();
    },
    c1TickStatus() {
      const fmtSec = (sec) => {
        const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s2 = sec % 60;
        return h > 0
          ? String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0')
          : String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0');
      };
      this.c1StatusElapsed = fmtSec(Math.floor((Date.now() - this.c1StatusSince) / 1000));
      this.c1ShiftElapsed  = fmtSec(Math.floor((Date.now() - this.c1ShiftStartTime) / 1000));
    },
    c1SaveTodayToLog() {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const workedMin = Math.floor((Date.now() - this.c1ShiftStartTime) / 60000);
        const log = JSON.parse(localStorage.getItem('c1_week_log') || '[]').filter(r => r.date !== today);
        log.unshift({ date: today, workedMin: Math.max(0, workedMin), bathroom: this.c1BathroomCount, coffee: this.c1CoffeeCount });
        localStorage.setItem('c1_week_log', JSON.stringify(log.slice(0, 14)));
        this.c1WeekLog = log.slice(0, 14);
      } catch(e) {}
    },
    s1FilterPanelClick(e) {
      // Panel stays open after selecting — user uses back (←) or X to close
    },
    c1TzTime(tz) {
      try {
        return new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz });
      } catch(e) { return "--:--"; }
    },
    c1Logout() {
      this.$store.dispatch('user/logOut');
    },

    // ── Data fetching ──────────────────────────────────────────────────────
    async loadTabData() {
      this.loading = true;
      try {
        const endpoint = this.selectedTab === 'shiftsNextWeek'
          ? 'shifts/get_current_week_shifts'
          : 'shifts/get_current_week_shifts_by_user';
        const res = await apiClientCreator(endpoint).get("");
        const normalized = this.normalizeShifts(res.data);
        if (normalized.length === 0) {
          // Fallback: try the broader all-users endpoint
          try {
            const fallback = await apiClientCreator("shifts/all_users_shifts").get("");
            this.rawShifts = this.normalizeShifts(fallback.data);
          } catch (_) {
            this.rawShifts = [];
          }
        } else {
          this.rawShifts = normalized;
        }
      } catch (e) {
        // Primary endpoint failed — fall back to all_users_shifts
        try {
          const fallback = await apiClientCreator("shifts/all_users_shifts").get("");
          this.rawShifts = this.normalizeShifts(fallback.data);
        } catch (_) {
          this.rawShifts = [];
        }
        console.error("Shifts load error:", e);
      } finally {
        this.loading = false;
      }
    },

    normalizeShifts(raw) {
      if (!Array.isArray(raw) || raw.length === 0) return [];
      if (Array.isArray(raw[0])) return raw;
      const map = new Map();
      raw.forEach(shift => {
        const uid = shift.user && shift.user[0] ? shift.user[0].id : '__unknown__';
        if (!map.has(uid)) map.set(uid, []);
        map.get(uid).push(shift);
      });
      return Array.from(map.values());
    },

    async fetchTotalSumForSlots() {
      try {
        const res = await apiClientCreator("shifts/sum_shifts_for_slots").get("");
        this.sumNight   = res.data.night   || 0;
        this.sumMorning = res.data.morning || 0;
        this.sumDay     = res.data.day     || 0;
        this.sumEvening = res.data.evening || 0;
      } catch (e) {}
    },

    async fetchUsersStrength() {
      try {
        const res = await apiClientCreator("shifts/check_user_strength").get("");
        this.userStrength = res.data || [];
      } catch (e) {}
    },

    async fetchShiftList() {
      try {
        const res = await apiClientCreator("shifts/list").get("");
        return res.data || [];
      } catch (e) { return []; }
    },

    async fetchTodayShifts() {
      try {
        const today = moment().format("YYYY-MM-DD");
        const res = await apiClientCreator(`shifts/all_users_shifts_for_today/${today}`).get("");
        return res.data || [];
      } catch (e) { return []; }
    },

    async fetchShiftGraph(type) {
      // type: 'this_week' | 'next_week' | 'came_early' | 'came_late' | 'came_on_time' | 'leave_early' | 'leave_on_time' | 'extra_hours'
      try {
        const graphType = type || 'this_week';
        const res = await apiClientCreator(`shifts/stats/graph/${graphType}`).get("");
        return res.data || {};
      } catch (e) { return {}; }
    },

    async approveUser(id, unApprove = false) {
      try {
        await apiClientCreator(`shifts/approve_all_shifts_for_single_user/${id}`).post("", { unApprove });
        this.loadTabData();
        this.fetchTotalSumForSlots();
        this.showToast(unApprove ? "Shifts unapproved" : "All shifts approved", "#22c55e");
      } catch (e) {}
    },

    async approveSingleShift(id) {
      try {
        await apiClientCreator(`shifts/approve_user_choice/${id}`).post("", {});
        this.loadTabData();
        this.showToast("Shift approved", "#22c55e");
      } catch (e) {}
    },

    async unapproveShift(id) {
      try {
        await apiClientCreator(`shifts/cancel_shift_approval/${id}`).post("", {});
        this.loadTabData();
        this.showToast("Shift unapproved", "#f87171");
      } catch (e) {}
    },

    async approveAllShifts() {
      try {
        await apiClientCreator("shifts/approve_all_shifts").get("");
        this.loadTabData();
        this.fetchTotalSumForSlots();
        this.showToast("All shifts approved", "#22c55e");
      } catch (e) {}
    },

    async copyLastWeek() {
      try {
        await apiClientCreator("shifts/copy_last_week_shifts").get("");
        this.loadTabData();
        this.fetchTotalSumForSlots();
        this.showToast("Last week's schedule copied", "#6366f1");
      } catch (e) {}
    },

    async submitSchedule() {
      if (!this.scheduleForm.choiceId || !this.scheduleForm.dayNumber) return;
      const uid = this.scheduleTarget ? this.userId(this.scheduleTarget) : null;
      if (!uid) return;
      try {
        await apiClientCreator("shifts/select_user_choice").post("", {
          user_id:   uid,
          choice_id: this.scheduleForm.choiceId,
          day:       this.scheduleForm.dayNumber,
          recurring: this.scheduleForm.recurring,
          notes:     this.scheduleForm.notes,
        });
        this.closeSchedule();
        this.loadTabData();
        this.fetchTotalSumForSlots();
        this.showToast("Shift assigned", "#22c55e");
      } catch (e) {}
    },

    async submitSwitchRequest() {
      if (!this.switchForm.scope || !this.switchForm.dayNumber) return;
      try {
        const myId = this.auth && this.auth.user ? this.auth.user.id : null;
        const targetId = this.switchTarget ? this.userId(this.switchTarget) : null;
        await apiClientCreator("shifts/select_user_choice").post("", {
          type: 'switch_request',
          from_user_id: myId,
          to_user_id: targetId,
          scope: this.switchForm.scope,
          day: this.switchForm.dayNumber,
          message: this.switchForm.message,
        });
        this.closeSwitchRequest();
        this.showToast("Switch request sent — Viki will notify them", "#6366f1");
      } catch (e) {
        this.closeSwitchRequest();
        this.showToast("Switch request sent", "#6366f1");
      }
    },

    respondSwitch(notification, response) {
      const responseMap = {
        accept: "Let's switch",
        decline: "I can't",
        later: "I'll update in 3 hours",
      };
      // Remove from list
      const idx = this.switchNotifications.indexOf(notification);
      if (idx > -1) this.switchNotifications.splice(idx, 1);
      this.showToast('Response sent: "' + responseMap[response] + '"', "#6366f1");
      // Notify VP Sales via API (best-effort)
      try {
        apiClientCreator("shifts/select_user_choice").post("", {
          type: 'switch_response',
          notification_id: notification.id,
          response,
        });
      } catch (e) {}
    },

    selfFillSlot(slot) {
      try {
        const myId = this.auth && this.auth.user ? this.auth.user.id : null;
        apiClientCreator("shifts/select_user_choice").post("", {
          user_id:   myId,
          choice_id: slot.choice_id,
          day:       slot.day_number,
        });
        this.openSlots = this.openSlots.filter(s => s !== slot);
        this.loadTabData();
        this.showToast("Slot claimed!", "#22c55e");
      } catch (e) {}
    },

    claimSlot(userShifts, dayId) {
      const myId = this.auth && this.auth.user ? this.auth.user.id : null;
      const s = userShifts.find(sh => sh.day_number === dayId);
      if (!s) return;
      try {
        apiClientCreator("shifts/select_user_choice").post("", {
          user_id:   myId,
          choice_id: s.choice_id,
          day:       dayId,
        });
        this.loadTabData();
        this.showToast("Shift claimed!", "#22c55e");
      } catch (e) {}
    },

    pollVikiAlerts() {
      this.buildVikiAlerts();
      this._vikiPoll = setInterval(() => {
        this.buildVikiAlerts();
      }, 2 * 60 * 1000); // every 2 min
    },

    buildVikiAlerts() {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const todayNum = now.getDay() === 0 ? 1 : now.getDay() + 1;
      const late = [];
      const noShow = [];

      this.rawShifts.forEach(u => {
        const name = this.userName(u);
        // Show only own data if agent
        if (!this.isManager && this.auth && this.auth.user) {
          const uid = u[0] && u[0].user && u[0].user[0] ? u[0].user[0].id : null;
          if (uid !== this.auth.user.id) return;
        }
        const s = u.find(sh => sh.day_number === todayNum);
        if (!s || s.choice_id === 6 || s.choice_id === 8) return;
        const startH = this.shiftStartH(s.choice_id);
        const nowMins = h * 60 + m;
        const startMins = startH * 60;
        if (nowMins >= startMins && nowMins < startMins + 90) {
          if (!s.clock_in) {
            if (nowMins > startMins + 15) {
              late.push({ name, minutesLate: nowMins - startMins, shiftType: this.shiftLabel(s), time: this.fmt2(h) + ':' + this.fmt2(m) });
            } else {
              noShow.push({ name, shiftType: this.shiftLabel(s), time: this.fmt2(h) + ':' + this.fmt2(m) });
            }
          }
        } else if (nowMins >= startMins + 90 && !s.clock_in) {
          noShow.push({ name, shiftType: this.shiftLabel(s), time: this.fmt2(h) + ':' + this.fmt2(m) });
        }
      });

      this.lateAlerts   = late;
      this.noShowAlerts = noShow;
    },

    fmt2(n) { return n.toString().padStart(2, '0'); },

    // ── Idle tracking ──────────────────────────────────────────────────────
    startIdleTracking() {
      const resetIdle = () => {
        this.lastActivity = Date.now();
        if (this.idleWarning) {
          this.idleWarning = false;
          clearInterval(this.idleCountdownTimer);
        }
      };
      document.addEventListener('mousemove', resetIdle);
      document.addEventListener('keydown', resetIdle);
      document.addEventListener('click', resetIdle);
      this._resetIdle = resetIdle;

      this.idleTimer = setInterval(() => {
        const elapsed = (Date.now() - this.lastActivity) / 1000 / 60;
        if (elapsed >= 60 && !this.idleWarning) {
          this.idleWarning = true;
          this.idleCountdown = 30;
          this.idleCountdownTimer = setInterval(() => {
            this.idleCountdown--;
            if (this.idleCountdown <= 0) {
              clearInterval(this.idleCountdownTimer);
              this.idleWarning = false;
              // Remove 60 min from hours and log out
              this.$store.dispatch('user/logOut');
            }
          }, 1000);
        }
      }, 60 * 1000);
    },

    stopIdleTracking() {
      clearInterval(this.idleTimer);
      clearInterval(this.idleCountdownTimer);
      if (this._resetIdle) {
        document.removeEventListener('mousemove', this._resetIdle);
        document.removeEventListener('keydown', this._resetIdle);
        document.removeEventListener('click', this._resetIdle);
      }
    },

    confirmPresence() {
      this.lastActivity = Date.now();
      this.idleWarning = false;
      clearInterval(this.idleCountdownTimer);
    },

    // ── Morning greeting ───────────────────────────────────────────────────
    checkMorningGreeting() {
      const h = new Date().getHours();
      if (h >= 7 && h <= 11 && this.auth && this.auth.user) {
        const name = (this.auth.user.first_name || '') + ' ' + (this.auth.user.last_name || '');
        const shiftKey = h < 9 ? 'Night' : 'Morning';
        this.morningBanner = { from: name.trim(), shift: shiftKey };
        setTimeout(() => { this.morningBanner = null; }, 15 * 60 * 1000);
      }
    },

    // ── Panel / row helpers ────────────────────────────────────────────────
    togglePanel(name) {
      this.panel = this.panel === name ? null : name;
    },
    s1PanelBtnClick() {
      this.panel = this.s1PanelIsOpen ? null : 'nav';
    },
    s1SelectPanel(p) {
      this.panel = p;
    },
    selectTab(alias) {
      this.selectedTab = alias;
      this.loadTabData();
    },

    toggleRow(idx) {
      this.expandedRow = this.expandedRow === idx ? null : idx;
      this.dropdownOpen = false;
    },

    toggleStatus(s) {
      const i = this.filterStatuses.indexOf(s);
      if (i > -1) this.filterStatuses.splice(i, 1);
      else this.filterStatuses.push(s);
    },

    setDatePreset(p) {
      this.datePreset = p;
    },

    // ── Dropdown ───────────────────────────────────────────────────────────
    openDropdown(day, choiceId, shiftI, userIdx) {
      if (this.dropdownOpen && this.dropdownDay === day && this.dropdownUserIdx === userIdx && this.dropdownShiftI === shiftI) {
        this.dropdownOpen = false;
        return;
      }
      this.dropdownDay = day;
      this.dropdownUserIdx = userIdx;
      this.dropdownShiftI = shiftI;
      this.dropdownOpen = true;
    },

    closeDropdown() { this.dropdownOpen = false; },

    onDropdownClose() {
      this.dropdownOpen = false;
      this.loadTabData();
      this.fetchTotalSumForSlots();
    },

    // ── Slide panels ───────────────────────────────────────────────────────
    openAgentDetail(userShifts) {
      this.agentDetailData = userShifts;
      this.agentDetailOpen = true;
    },

    closeAgentDetail() {
      this.agentDetailOpen = false;
      this.agentDetailData = null;
    },

    openSchedulePanel(userShifts) {
      this.scheduleTarget = userShifts;
      this.scheduleForm = { choiceId: null, dayNumber: null, recurring: false, notes: "" };
      this.scheduleOpen = true;
    },

    closeSchedule() {
      this.scheduleOpen = false;
      this.scheduleTarget = null;
    },

    openSwitchRequest(userShifts) {
      this.switchTarget = userShifts;
      this.switchForm = { scope: 'day', dayNumber: null, message: "" };
      this.switchRequestOpen = true;
    },

    closeSwitchRequest() {
      this.switchRequestOpen = false;
      this.switchTarget = null;
    },

    openCalDayDetail(userShifts, dayId) {
      if (this.calDayDetail && this.calDayDetail.dayId === dayId && this.calDayDetail.userShifts === userShifts) {
        this.calDayDetail = null;
      } else {
        this.calDayDetail = { userShifts, dayId };
      }
    },

    // ── Toast ──────────────────────────────────────────────────────────────
    showToast(msg, color = "#6366f1") {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { msg, color };
      this.toastTimer = setTimeout(() => { this.toast = null; }, 3000);
    },

    // ── CSV Export ──────────────────────────────────────────────────────────
    s1ExportCSV() {
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const dayIds = [2, 3, 4, 5, 6, 7, 1];
      const header = ['User', 'Role', ...dayNames.map((d, i) => d + ' ' + this.getDateLabel(dayIds[i]))];
      const rows = [header.join(',')];

      this.rawShifts.forEach(u => {
        const name = this.userName(u).replace(/,/g, ' ');
        const role = (this.userRole(u) || 'Agent').replace(/,/g, ' ');
        const dayCells = dayIds.map(dayId => {
          const s = u.find(sh => sh.day_number === dayId);
          if (!s) return '-';
          const label = this.shiftLabel(s);
          const status = this.computeAttendanceStatus(s);
          return label + ' (' + status.replace(/_/g, ' ') + ')';
        });
        rows.push([name, role, ...dayCells].join(','));
      });

      const csv = rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const weekLabel = this.selectedTab === 'shiftsNextWeek' ? 'next-week' : 'this-week';
      link.href = url;
      link.download = 'shifts-' + weekLabel + '-' + moment().format('YYYY-MM-DD') + '.csv';
      link.click();
      URL.revokeObjectURL(url);
      this.showToast('CSV exported', '#22c55e');
    },

    // ── Data helpers ───────────────────────────────────────────────────────
    userName(userShifts) {
      if (!userShifts || !userShifts[0]) return "Unknown";
      const u = userShifts[0].user;
      if (Array.isArray(u) && u[0]) return u[0].full_name || "Unknown";
      if (u && u.full_name) return u.full_name;
      return "Unknown";
    },

    userId(userShifts) {
      if (!userShifts || !userShifts[0]) return null;
      const u = userShifts[0].user;
      if (Array.isArray(u) && u[0]) return u[0].id;
      if (u) return u.id;
      return null;
    },

    userRole(userShifts) {
      if (!userShifts || !userShifts[0]) return "";
      const u = userShifts[0].user;
      const usr = Array.isArray(u) ? u[0] : u;
      return usr ? (usr.role || "") : "";
    },

    userTeamId(userShifts) {
      if (!userShifts || !userShifts[0]) return null;
      const u = userShifts[0].user;
      const usr = Array.isArray(u) ? u[0] : u;
      return usr ? (usr.team_id || null) : null;
    },

    todayShift(userShifts) {
      const now = new Date();
      const todayNum = now.getDay() === 0 ? 1 : now.getDay() + 1;
      return userShifts.find(s => s.day_number === todayNum) || null;
    },

    isOnShiftNow(userShifts) {
      const s = this.todayShift(userShifts);
      if (!s) return false;
      return this.isHourInShift(new Date().getHours(), s.choice_id);
    },

    isHourInShift(h, choiceId) {
      if (choiceId === 5) return h >= 23 || h < 9;   // Night
      if (choiceId === 1) return h >= 9  && h < 17;  // Morning
      if (choiceId === 2) return h >= 11 && h < 19;  // Day
      if (choiceId === 3) return h >= 15 && h < 23;  // Evening
      return false;
    },

    shiftStartH(choiceId) {
      const map = { 5: 23, 1: 9, 2: 11, 3: 15 };
      return map[choiceId] || 9;
    },

    shiftLabel(u) {
      if (!u) return "—";
      const map = { 5:"NIT", 1:"MOR", 2:"DAY", 3:"EVE", 4:"ANY", 6:"VAC", 7:"CUS", 8:"ILL" };
      return map[u.choice_id] || u.name || "—";
    },

    shiftBadgeClass(u) {
      if (!u) return "";
      const map = { 5:"s1-b-nit", 1:"s1-b-mor", 2:"s1-b-day", 3:"s1-b-eve", 4:"s1-b-any", 6:"s1-b-vac", 7:"s1-b-cus", 8:"s1-b-ill" };
      return map[u.choice_id] || "";
    },

    shiftTime(s) {
      if (!s) return "—";
      const times = { 5:"23:00–09:00", 1:"09:00–17:00", 2:"11:00–19:00", 3:"15:00–23:00", 4:"Flexible", 6:"Vacation", 8:"Illness" };
      return times[s.choice_id] || "—";
    },

    getShiftTimeForDay(userShifts, dayId) {
      const s = userShifts.find(sh => sh.day_number === dayId);
      return s ? this.shiftTime(s) : "No shift";
    },

    isEmptySlot(userShifts, dayId) {
      return !userShifts.find(sh => sh.day_number === dayId);
    },

    computeAttendanceStatus(s) {
      if (!s) return 'not_on_shift';
      if (s.choice_id === 6) return 'vacation';
      if (s.choice_id === 8) return 'ill';
      if (!s.clock_in) return 'absent';
      const startH = this.shiftStartH(s.choice_id);
      const startMins = startH * 60;
      const clockInParts = (s.clock_in || '').split(':');
      if (clockInParts.length >= 2) {
        const ciMins = parseInt(clockInParts[0]) * 60 + parseInt(clockInParts[1]);
        if (ciMins < startMins - 5) return 'early_bird';
        if (ciMins > startMins + 15) return 'late';
      }
      return 'on_time';
    },

    statusLabel(s) {
      if (!s) return "—";
      const st = this.computeAttendanceStatus(s);
      const map = {
        on_time: "On Time", late: "Late", absent: "Absent",
        early_bird: "Early Bird", not_on_shift: "Not on shift",
        vacation: "Vacation", ill: "Ill",
      };
      return map[st] || "Pending";
    },

    statusChipClass(s) {
      if (!s) return "s1-status-none";
      const st = this.computeAttendanceStatus(s);
      const map = {
        on_time: "s1-status-on-time", late: "s1-status-late", absent: "s1-status-absent",
        early_bird: "s1-status-early", not_on_shift: "s1-status-none",
        vacation: "s1-status-vac", ill: "s1-status-absent",
      };
      return map[st] || "s1-status-pending";
    },

    getDayShiftClass(userShifts, dayId) {
      const s = userShifts.find(sh => sh.day_number === dayId);
      return s ? this.shiftBadgeClass(s) : "s1-b-empty";
    },

    getDayShiftLabel(userShifts, dayId) {
      const s = userShifts.find(sh => sh.day_number === dayId);
      return s ? this.shiftLabel(s) : "—";
    },

    calcHours(s) {
      if (!s || !s.clock_in || !s.clock_out) return "—";
      try {
        const [ih, im] = s.clock_in.split(':').map(Number);
        const [oh, om] = s.clock_out.split(':').map(Number);
        let mins = (oh * 60 + om) - (ih * 60 + im);
        if (mins < 0) mins += 24 * 60; // overnight shift
        if (s.break_duration) {
          const [bh, bm] = s.break_duration.split(':').map(Number);
          mins -= bh * 60 + bm;
        }
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m > 0 ? h + 'h' + m + 'm' : h + 'h';
      } catch (e) { return "—"; }
    },

    rawHours(s) {
      if (!s || !s.clock_in || !s.clock_out) return 0;
      try {
        const [ih, im] = s.clock_in.split(':').map(Number);
        const [oh, om] = s.clock_out.split(':').map(Number);
        let mins = (oh * 60 + om) - (ih * 60 + im);
        if (mins < 0) mins += 24 * 60;
        return mins / 60;
      } catch (e) { return 0; }
    },

    weekHours(userShifts) {
      return Math.round(userShifts.reduce((a, s) => a + this.rawHours(s), 0) * 10) / 10;
    },

    dayName(id) {
      const map = { 1:"Sun", 2:"Mon", 3:"Tue", 4:"Wed", 5:"Thu", 6:"Fri", 7:"Sat" };
      return map[id] || "?";
    },

    getDateLabel(dayId) {
      const map = { 2:0, 3:1, 4:2, 5:3, 6:4, 7:5, 1:6 };
      const add = map[dayId] !== undefined ? map[dayId] : 0;
      if (this.selectedTab === "shiftsNextWeek") {
        return moment().startOf("isoWeek").add(7 + add, "days").format("DD/MM");
      }
      return moment().startOf("isoWeek").add(add, "days").format("DD/MM");
    },

    shiftBarPct(val) {
      const max = Math.max(this.sumNight, this.sumMorning, this.sumDay, this.sumEvening, 1);
      return Math.round(((val || 0) / max) * 100);
    },

    hourBarPct(hours) {
      const maxH = Math.max(...this.hoursPerAgent.map(a => a.hours), 1);
      return Math.round((hours / maxH) * 100);
    },

    donArc(part, total) {
      if (!total) return 0;
      return Math.round((part / total) * 88);
    },

    punctColor(score) {
      if (!score && score !== 0) return "var(--c1-t3, #5a5a78)";
      if (score >= 90) return "#22c55e";
      if (score >= 70) return "#f59e0b";
      return "#ef4444";
    },

    initials(name) {
      if (!name) return "?";
      return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    },

    shortName(name) {
      if (!name) return "?";
      const parts = name.split(" ");
      if (parts.length < 2) return name;
      return parts[0] + " " + parts[1][0] + ".";
    },

    userColor(name) {
      if (!name) return COLORS[0];
      let n = 0;
      for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
      return COLORS[n % COLORS.length];
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════════════════════ ROOT */
/* c1-tib is position:sticky so it's in document flow — no padding-top needed */
.c1-main { padding-top: 0; }
/* Hide sliding panel — replaced by inline top-strip */
.c1-shifts-page .c1-panel { display: none !important; }
/* Spacer that compensates for the fixed week-bar (38px) sitting on top of content */
.s1-wk-spacer { height: 38px; flex-shrink: 0; }

/* ── Week bar accordion (mobile) ──────────────────────────────── */
/* Desktop: hide toggle, keep items as horizontal row */
.s1-wb-toggle { display: none; }
.s1-wb-items { display: flex; flex: 1; align-items: center; gap: 8px; }

@media (max-width: 767px) {
  /* Bar stays fixed 38px; dropdown overlays below */
  .s1-week-bar { overflow: visible; padding: 0; }

  /* Toggle row fills the bar */
  .s1-wb-toggle {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; height: 38px; padding: 0 14px; cursor: pointer;
    color: var(--c1-t1, #e8e8f0); font-size: 12px; font-weight: 600;
  }
  .s1-wb-arr { transition: transform .2s ease; flex-shrink: 0; }
  .s1-wb-arr.open { transform: rotate(180deg); }

  /* Items dropdown: hidden by default, visible when .open */
  .s1-wb-items {
    display: none;
    position: absolute;
    top: 38px; left: 0; right: 0;
    flex-direction: column; align-items: stretch;
    gap: 4px; padding: 8px 14px 12px;
    background: var(--c1-surf, #13131a);
    border-bottom: 1px solid var(--c1-brd, #1e1e2e);
    z-index: 98;
    box-shadow: 0 6px 18px rgba(0,0,0,.3);
  }
  .s1-wb-items.open { display: flex; }

  /* Items: stack tabs + actions vertically */
  .s1-wb-items .s1-week-tabs { flex-direction: column; gap: 2px; }
  .s1-wb-items .s1-week-tab { width: 100%; padding: 9px 12px; font-size: 13px; border-radius: 8px; }
  .s1-wb-items .c1-hp { display: none; }
  .s1-wb-items .s1-week-action { width: 100%; padding: 9px 12px; font-size: 12px; border-radius: 8px; justify-content: flex-start; }
}

/* ══════════════════════════════════════════════════════ TOAST */
.sh1-toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  z-index: 99999; padding: 10px 22px; border-radius: 24px; color: #fff;
  font-size: .85rem; font-weight: 600;
  box-shadow: 0 4px 20px rgba(0,0,0,.25); pointer-events: none;
  white-space: nowrap; height: auto; display: inline-flex; align-items: center;
}
.sh1-toast-fade-enter-active, .sh1-toast-fade-leave-active { transition: opacity .2s, transform .2s; }
.sh1-toast-fade-enter, .sh1-toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }

/* ══════════════════════════════════════════════════════ IDLE OVERLAY */
.s1-idle-overlay {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(0,0,0,.75); display: grid; place-items: center;
}
.s1-idle-box {
  background: var(--c1-surf, #13131a); border: 1px solid var(--c1-brd, #1e1e2e);
  border-radius: 14px; padding: 32px 28px; max-width: 360px; text-align: center;
}
.s1-idle-icon svg { width: 40px; height: 40px; stroke: #f59e0b; fill: none; stroke-width: 1.5; margin-bottom: 12px; }
.s1-idle-title { font-size: 18px; font-weight: 700; margin-bottom: 8px; color: var(--c1-t1, #e8e8f0); }
.s1-idle-sub { font-size: 12px; color: var(--c1-t3, #5a5a78); margin-bottom: 20px; line-height: 1.5; }
.s1-idle-btn {
  display: block; width: 100%; padding: 10px 0; border: none; border-radius: 8px;
  background: #6366f1; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; margin-bottom: 10px;
}
.s1-idle-countdown { font-size: 11px; color: #ef4444; }
.s1-fade-enter-active, .s1-fade-leave-active { transition: opacity .3s; }
.s1-fade-enter, .s1-fade-leave-to { opacity: 0; }

/* ══════════════════════════════════════════════════════ MORNING BANNER */
.s1-morning-banner {
  position: fixed;
  top: 82px; /* below c1-tib (44px) + s1-week-bar (38px) */
  left: var(--gnm-sw, 220px);
  right: 0;
  z-index: 97;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; background: linear-gradient(90deg, rgba(99,102,241,.15), rgba(99,102,241,.05));
  border-bottom: 1px solid rgba(99,102,241,.2);
}
@media (max-width: 768px) { .s1-morning-banner { left: 0; } }
.s1-mb-av { width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center; font-size: 10px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-mb-text { flex: 1; }
.s1-mb-greeting { font-weight: 700; font-size: 12px; display: block; }
.s1-mb-msg { font-size: 11px; color: var(--c1-t3, #5a5a78); }
.s1-mb-close { background: none; border: none; cursor: pointer; padding: 4px; }
.s1-mb-close svg { width: 14px; height: 14px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; }
.s1-slide-down-enter-active, .s1-slide-down-leave-active { transition: max-height .25s ease, opacity .2s; max-height: 80px; }
.s1-slide-down-enter, .s1-slide-down-leave-to { max-height: 0; opacity: 0; overflow: hidden; }

/* ══════════════════════════════════════════════════════ TOP BAR */
.c1-tib {
  display: flex; align-items: center; gap: 2px; padding: 0 10px 0 0; height: 44px;
  background: var(--c1-surf, #13131a); border-bottom: 1px solid var(--c1-brd, #1e1e2e);
  position: sticky; top: 0; z-index: 100; overflow-x: auto; scrollbar-width: none;
}
.c1-tib::-webkit-scrollbar { display: none; }
.c1-tib-btn {
  display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px;
  border-radius: 6px; font-size: 12px; font-weight: 500; color: var(--c1-t2, #9090a8);
  cursor: pointer; white-space: nowrap; transition: background .12s, color .12s;
  position: relative; border: 1px solid transparent; flex-shrink: 0;
}
.c1-tib-btn svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.c1-tib-btn:hover { background: rgba(99,102,241,.1); color: var(--c1-t1, #e8e8f0); }
.c1-tib-btn.on { background: rgba(99,102,241,.18); color: #a5b4fc; border-color: rgba(99,102,241,.25); }
.s1-viki-alert { animation: s1-viki-pulse 2s infinite; }
@keyframes s1-viki-pulse { 0%,100% { border-color: transparent; } 50% { border-color: rgba(239,68,68,.4); } }
.c1-tib-badge {
  font-size: 9px; font-weight: 700; background: var(--c1-accent, #6366f1);
  color: #fff; border-radius: 8px; padding: 1px 5px; min-width: 16px; text-align: center;
}
.s1-badge-red { background: #ef4444; }
.c1-hp { flex: 1; min-width: 4px; }
.c1-tib-user {
  width: 30px; height: 30px; border-radius: 50%; background: var(--c1-accent, #6366f1);
  display: grid; place-items: center; cursor: pointer; position: relative; flex-shrink: 0;
  border: 2px solid transparent; transition: border-color .12s;
}
.c1-tib-user.on { border-color: #a5b4fc; }
.c1-tib-ini { font-size: 11px; font-weight: 700; color: #fff; }
.c1-tib-udot { width: 8px; height: 8px; border-radius: 50%; background: var(--c1-brd, #1e1e2e); border: 1.5px solid var(--c1-surf, #13131a); position: absolute; bottom: -1px; right: -1px; }
.c1-tib-udot.online { background: #22c55e; }
/* week selector sub-header */
.s1-week-bar { position: fixed; top: 44px; left: var(--gnm-sw, 220px); right: 0; z-index: 99; height: 38px; display: flex; align-items: center; gap: 8px; padding: 0 14px; background: var(--c1-surf, #13131a); border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
@media (max-width: 768px) { .s1-week-bar { left: 0; } }
.s1-week-tabs { display: flex; gap: 2px; }
.s1-week-tab { padding: 5px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; color: var(--c1-t2, #9090a8); cursor: pointer; transition: background .1s, color .1s; white-space: nowrap; }
.s1-week-tab:hover { background: rgba(255,255,255,.05); color: var(--c1-t1, #e8e8f0); }
.s1-week-tab.on { background: rgba(99,102,241,.2); color: #a5b4fc; }
.s1-week-action { display: flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; color: var(--c1-t2, #9090a8); cursor: pointer; border: 1px solid var(--c1-brd, #1e1e2e); background: var(--c1-card, #17172a); transition: all .15s; white-space: nowrap; }
.s1-week-action:hover { color: var(--c1-t1, #e8e8f0); border-color: rgba(99,102,241,.4); }
.s1-week-action-approve { color: #22c55e; border-color: rgba(34,197,94,.3); }
.s1-week-action-approve:hover { background: rgba(34,197,94,.08); }

/* ══════════════════════════════════════════════════════ PANELS */
/* .c1-panel positioning handled globally by c1-design.css */
.c1-panel.c1-panel--open { transform: translateX(0); box-shadow: -4px 0 24px rgba(0,0,0,.4); }
.c1-sl-body { display: flex; flex-wrap: wrap; gap: 0; padding: 12px 14px 4px; overflow-y: auto; }
.c1-fp-group { min-width: 160px; flex: 1; padding: 0 8px 12px 0; }
.c1-fp-full { flex: 100%; }
.c1-fp-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--c1-t3, #5a5a78); margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
.c1-fp-opts { display: flex; flex-wrap: wrap; gap: 4px; }
.c1-fp-opt { padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 500; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); color: var(--c1-t2, #9090a8); cursor: pointer; transition: all .1s; white-space: nowrap; }
.c1-fp-opt:hover { background: rgba(99,102,241,.1); color: var(--c1-t1, #e8e8f0); }
.c1-fp-opt.on { background: rgba(99,102,241,.2); border-color: rgba(99,102,241,.4); color: #a5b4fc; }
.s1-chip-on-time.on { background: rgba(34,197,94,.15); border-color: rgba(34,197,94,.4); color: #4ade80; }
.s1-chip-late.on    { background: rgba(234,179,8,.15); border-color: rgba(234,179,8,.4); color: #fbbf24; }
.s1-chip-absent.on  { background: rgba(239,68,68,.15); border-color: rgba(239,68,68,.4); color: #f87171; }
.s1-chip-early.on   { background: rgba(168,85,247,.15); border-color: rgba(168,85,247,.4); color: #c084fc; }
.c1-fp-search { display: flex; align-items: center; gap: 6px; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; padding: 6px 10px; max-width: 280px; }
.c1-fp-sico { width: 13px; height: 13px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; flex-shrink: 0; }
.c1-fp-sinput { flex: 1; background: none; border: none; outline: none; font-size: 12px; color: var(--c1-t1, #e8e8f0); font-family: inherit; }
.c1-fp-sinput::placeholder { color: var(--c1-t3, #5a5a78); }
.c1-fp-sclear { cursor: pointer; display: grid; place-items: center; }
.c1-fp-sclear svg { width: 12px; height: 12px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; }
.c1-collapse-bar { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 7px; font-size: 11px; color: var(--c1-t3, #5a5a78); cursor: pointer; border-top: 1px solid var(--c1-brd, #1e1e2e); transition: background .1s; }
.c1-collapse-bar:hover { background: rgba(255,255,255,.03); color: var(--c1-t1, #e8e8f0); }
.c1-collapse-bar svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; }

/* ══════════════════════════════════════════════════════ CHARTS */
.c1-co-lbl { width: 100%; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--c1-t3, #5a5a78); margin-bottom: 8px; }
.c1-kpi-row { display: flex; flex-wrap: wrap; gap: 8px; width: 100%; margin-bottom: 4px; }
.c1-kpi { background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 8px; padding: 10px 14px; flex: 1; min-width: 70px; }
.c1-kpi-lbl { font-size: 10px; color: var(--c1-t3, #5a5a78); margin-bottom: 4px; }
.c1-kpi-val { font-size: 22px; font-weight: 700; color: var(--c1-t1, #e8e8f0); font-variant-numeric: tabular-nums; }
.s1-c-nit { color: #a78bfa !important; } .s1-c-mor { color: #60a5fa !important; }
.s1-c-day { color: #4ade80 !important; } .s1-c-eve { color: #fb923c !important; }
.s1-c-ok  { color: #22c55e !important; }

.s1-punct-agent-bars { width: 100%; display: flex; flex-direction: column; gap: 6px; }
.s1-pab-row { display: flex; align-items: center; gap: 8px; }
.s1-pab-av { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; font-size: 8px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-pab-name { font-size: 11px; color: var(--c1-t2, #9090a8); width: 70px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.s1-pab-track { flex: 1; height: 8px; background: var(--c1-card, #17172a); border-radius: 4px; overflow: hidden; }
.s1-pab-fill { height: 100%; border-radius: 4px; transition: width .4s ease; }
.s1-pab-val { font-size: 10px; width: 34px; text-align: right; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.s1-pab-empty { font-size: 11px; color: var(--c1-t3, #5a5a78); padding: 8px 0; }

.s1-hours-bars { width: 100%; display: flex; flex-direction: column; gap: 6px; }
.s1-hb-row { display: flex; align-items: center; gap: 8px; }
.s1-hb-av { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; font-size: 8px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-hb-name { font-size: 11px; color: var(--c1-t2, #9090a8); width: 70px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.s1-hb-track { flex: 1; height: 8px; background: var(--c1-card, #17172a); border-radius: 4px; overflow: hidden; }
.s1-hb-fill { height: 100%; border-radius: 4px; background: #6366f1; transition: width .4s ease; }
.s1-hb-val { font-size: 10px; width: 30px; text-align: right; font-variant-numeric: tabular-nums; flex-shrink: 0; color: var(--c1-t2, #9090a8); }

.s1-coverage-grid { display: flex; gap: 0; width: 100%; overflow-x: auto; border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; }
.s1-cov-col { flex-shrink: 0; width: 40px; border-right: 1px solid var(--c1-brd, #1e1e2e); display: flex; flex-direction: column; align-items: center; }
.s1-cov-col:last-child { border-right: none; }
.s1-cov-agents { padding: 4px; display: flex; flex-direction: column; gap: 2px; min-height: 40px; align-items: center; }
.s1-cov-dot { width: 10px; height: 10px; border-radius: 50%; cursor: default; }
.s1-cov-hour { font-size: 8px; color: var(--c1-t3, #5a5a78); padding: 2px 0 3px; text-align: center; border-top: 1px solid var(--c1-brd, #1e1e2e); width: 100%; font-family: 'JetBrains Mono', monospace; }

.c1-vbar-chart { width: 100%; display: flex; flex-direction: column; gap: 6px; }
.c1-vbar-row { display: flex; align-items: center; gap: 8px; }
.c1-vbar-lbl { font-size: 10px; color: var(--c1-t3, #5a5a78); width: 52px; flex-shrink: 0; }
.c1-vbar-track { flex: 1; height: 16px; background: var(--c1-card, #17172a); border-radius: 4px; overflow: hidden; border: 1px solid var(--c1-brd, #1e1e2e); }
.c1-vbar-fill { height: 100%; border-radius: 4px; display: flex; align-items: center; padding: 0 6px; min-width: 20px; transition: width .4s ease; }
.c1-vbar-val { font-size: 9px; color: #fff; font-weight: 700; white-space: nowrap; }

.c1-don-wrap-lg { display: flex; align-items: center; gap: 16px; width: 100%; }
.c1-don-lg { position: relative; width: 64px; height: 64px; flex-shrink: 0; }
.c1-don-lg svg { width: 100%; height: 100%; }
.c1-don-c-lg { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-size: 13px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }
.c1-don-leg-lg { display: flex; flex-direction: column; gap: 6px; }
.c1-don-i-lg { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--c1-t2, #9090a8); }
.c1-don-d-lg { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.c1-don-v-lg { margin-left: 4px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }

/* ══════════════════════════════════════════════════════ CALENDAR */
.c1-cal-panel { padding-bottom: 0; }
.c1-cal-panel-hdr { display: flex; align-items: flex-start; gap: 8px; padding: 12px 14px 10px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.c1-cal-panel-hdr svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; }
.c1-cal-panel-hdr > div > span { font-weight: 700; font-size: 13px; }
.c1-cal-panel-sub { font-size: 11px; color: var(--c1-t3, #5a5a78); margin-top: 2px; }
.c1-cal-panel-close { display: inline-flex; align-items: center; gap: 4px; background: none; border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; padding: 4px 10px; font-size: 11px; color: var(--c1-t2, #9090a8); cursor: pointer; transition: all .1s; flex-shrink: 0; }
.c1-cal-panel-close:hover { background: rgba(255,255,255,.05); color: var(--c1-t1, #e8e8f0); }
.c1-cal-panel-close svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2; }

.s1-cal-legend { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 14px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cal-leg-item { font-size: 10px; padding: 2px 8px; border-radius: 4px; font-weight: 600; }

.s1-cal-grid { overflow-x: auto; }
.s1-cal-head-row { display: grid; grid-template-columns: 110px repeat(7, 1fr); border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cal-head-cell { padding: 6px 8px; text-align: center; border-right: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cal-head-cell:last-child { border-right: none; }
.s1-cal-day-name { font-size: 11px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }
.s1-cal-day-date { font-size: 9px; color: var(--c1-t3, #5a5a78); font-family: 'JetBrains Mono', monospace; }
.s1-cal-user-row { display: grid; grid-template-columns: 110px repeat(7, 1fr); border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cal-user-row:last-child { border-bottom: none; }
.s1-cal-row-label { display: flex; align-items: center; gap: 6px; padding: 6px 8px; border-right: 1px solid var(--c1-brd, #1e1e2e); min-width: 0; }
.s1-cal-row-av { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; font-size: 8px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-cal-row-name { font-size: 10px; color: var(--c1-t1, #e8e8f0); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.s1-cal-cell { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 6px 4px; border-right: 1px solid var(--c1-brd, #1e1e2e); cursor: pointer; transition: background .1s; min-width: 60px; position: relative; }
.s1-cal-cell:hover { background: rgba(255,255,255,.03); }
.s1-cal-cell:last-child { border-right: none; }
.s1-cal-cell-label { font-size: 10px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.s1-cal-cell-claim { position: absolute; top: 2px; right: 2px; }
.s1-cal-cell-claim svg { width: 10px; height: 10px; stroke: #22c55e; fill: none; stroke-width: 2.5; }
.s1-cal-loading { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 30px; color: var(--c1-t3, #5a5a78); font-size: 12px; grid-column: 1 / -1; }

/* Calendar day detail */
.s1-cal-day-detail { background: var(--c1-surf, #13131a); border-top: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cdd-hdr { display: flex; align-items: center; padding: 10px 14px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.s1-cdd-title { font-size: 13px; font-weight: 700; flex: 1; }
.s1-cdd-close { background: none; border: none; cursor: pointer; }
.s1-cdd-close svg { width: 14px; height: 14px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; }
.s1-cdd-body { padding: 12px 14px; }
.s1-cdd-agent-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.s1-cdd-av { width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-cdd-name { font-weight: 600; font-size: 13px; }
.s1-cdd-role { font-size: 10px; color: var(--c1-t3, #5a5a78); text-transform: capitalize; }
.s1-cdd-shift-time { font-size: 12px; color: var(--c1-t2, #9090a8); margin-bottom: 10px; font-family: 'JetBrains Mono', monospace; }
.s1-cdd-actions { display: flex; gap: 6px; }

/* ══════════════════════════════════════════════════════ VIKI */
.s1-viki-panel { }
.s1-viki-hdr { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.s1-viki-icon svg { width: 22px; height: 22px; stroke: #a5b4fc; fill: none; stroke-width: 1.5; }
.s1-viki-title { font-weight: 700; font-size: 14px; display: flex; align-items: center; gap: 6px; }
.s1-viki-badge { font-size: 9px; background: #6366f1; color: #fff; padding: 1px 6px; border-radius: 4px; font-weight: 700; }
.s1-viki-sub { font-size: 10px; color: var(--c1-t3, #5a5a78); }
.s1-viki-body { padding: 10px 14px; max-height: 400px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px; }
.s1-viki-section { display: flex; flex-direction: column; gap: 6px; }
.s1-viki-section-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; display: flex; align-items: center; gap: 6px; margin-bottom: 2px; }
.s1-viki-section-lbl svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; }
.s1-viki-lbl-warn   { color: #f59e0b; }
.s1-viki-lbl-danger { color: #ef4444; }
.s1-viki-lbl-info   { color: #60a5fa; }
.s1-viki-alert-row { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 7px; }
.s1-alert-late   { background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.2); }
.s1-alert-absent { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.2); }
.s1-va-av { width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; font-size: 9px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-va-text { flex: 1; font-size: 12px; color: var(--c1-t1, #e8e8f0); }
.s1-va-time { font-size: 10px; color: var(--c1-t3, #5a5a78); font-family: 'JetBrains Mono', monospace; flex-shrink: 0; }
.s1-viki-notif-row { background: rgba(96,165,250,.08); border: 1px solid rgba(96,165,250,.2); border-radius: 7px; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.s1-vn-text { font-size: 12px; color: var(--c1-t1, #e8e8f0); }
.s1-vn-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.s1-vn-btn { padding: 5px 10px; border-radius: 5px; font-size: 11px; font-weight: 600; border: 1px solid; cursor: pointer; transition: all .1s; }
.s1-vn-yes { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.3); color: #4ade80; }
.s1-vn-no { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.3); color: #f87171; }
.s1-vn-later { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.3); color: #a5b4fc; }
.s1-viki-clear { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px; color: #22c55e; text-align: center; }
.s1-viki-clear svg { width: 28px; height: 28px; stroke: currentColor; fill: none; stroke-width: 2; }
.s1-viki-clear div { font-size: 12px; color: var(--c1-t3, #5a5a78); }
.s1-viki-slot-row { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: rgba(34,197,94,.06); border: 1px solid rgba(34,197,94,.15); border-radius: 6px; }
.s1-slot-day { font-size: 12px; font-weight: 600; color: var(--c1-t1, #e8e8f0); width: 30px; }
.s1-slot-time { flex: 1; font-size: 11px; color: var(--c1-t3, #5a5a78); font-family: 'JetBrains Mono', monospace; }
.s1-slot-claim { padding: 4px 12px; background: #22c55e; color: #fff; border: none; border-radius: 5px; font-size: 11px; font-weight: 600; cursor: pointer; }

/* ══════════════════════════════════════════════════════ PROFILE PANEL */
.c1-prof-panel { background: var(--c1-surf, #13131a); border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.c1-prof-head { padding: 16px 16px 10px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); }
.c1-prof-brand-row { display: flex; align-items: center; margin-bottom: 10px; }
.c1-prof-brand-name { font-size: 16px; font-weight: 800; color: var(--c1-t1, #e8e8f0); letter-spacing: -.3px; }
.c1-prof-brand-name span { color: #6366f1; }
.c1-prof-user-row { display: flex; align-items: center; gap: 10px; }
.c1-prof-av { width: 38px; height: 38px; border-radius: 50%; background: #6366f1; display: grid; place-items: center; font-size: 14px; font-weight: 700; color: #fff; flex-shrink: 0; }
.c1-prof-name { font-size: 13px; font-weight: 600; color: var(--c1-t1, #e8e8f0); }
.c1-prof-role { font-size: 10px; color: var(--c1-t3, #5a5a78); text-transform: capitalize; }
.c1-prof-scroll { padding: 8px 0 4px; }
.c1-nc-lbl { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--c1-t3, #5a5a78); padding: 8px 16px 4px; }
.c1-ng { display: flex; flex-wrap: wrap; gap: 4px; padding: 0 12px 8px; }
.c1-nb { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 7px; font-size: 12px; font-weight: 500; text-decoration: none; color: var(--c1-t1, #e8e8f0); background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); transition: all .1s; cursor: pointer; }
.c1-nb svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.c1-nb:hover { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.3); }
.c1-nb-dp:hover { background: rgba(124,92,252,.12); border-color: rgba(124,92,252,.35); color: #b8a4fc; }
.c1-nb-logout { color: var(--c1-t3, #5a5a78); }
.c1-nb-logout:hover { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.3); color: #f87171; }

/* ══════════════════════════════════════════════════════ ON NOW STRIP */
.s1-on-now-strip { margin-top: 0; }
.s1-on-now-strip { display: flex; align-items: center; gap: 10px; padding: 8px 14px; background: var(--c1-surf, #13131a); border-bottom: 1px solid var(--c1-brd, #1e1e2e); overflow: hidden; }
.s1-on-now-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--c1-t3, #5a5a78); white-space: nowrap; flex-shrink: 0; }
.s1-on-now-scroll { display: flex; gap: 6px; overflow-x: auto; scrollbar-width: none; flex: 1; }
.s1-on-now-scroll::-webkit-scrollbar { display: none; }
.s1-on-now-chip { display: flex; align-items: center; gap: 5px; padding: 3px 8px 3px 3px; border-radius: 20px; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); flex-shrink: 0; opacity: .55; transition: opacity .2s, border-color .2s; }
.s1-on-now-chip.active { opacity: 1; border-color: rgba(34,197,94,.35); }
.s1-on-now-av { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; font-size: 8px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-on-now-name { font-size: 11px; font-weight: 500; color: var(--c1-t1, #e8e8f0); white-space: nowrap; }
.s1-on-now-dot { width: 6px; height: 6px; border-radius: 50%; background: #22c55e; flex-shrink: 0; animation: s1pulse 2s infinite; }
.s1-on-now-empty { font-size: 11px; color: var(--c1-t3, #5a5a78); }
@keyframes s1pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .6; transform: scale(.85); } }

/* ══════════════════════════════════════════════════════ MAIN CONTENT */
.s1-content { padding: 12px 12px 60px; }
.s1-loading { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 60px 0; color: var(--c1-t3, #5a5a78); font-size: 13px; }
.s1-spinner { width: 20px; height: 20px; border: 2px solid var(--c1-brd, #1e1e2e); border-top-color: #6366f1; border-radius: 50%; animation: s1spin .7s linear infinite; flex-shrink: 0; }
@keyframes s1spin { to { transform: rotate(360deg); } }
.s1-empty { text-align: center; padding: 60px 20px; color: var(--c1-t3, #5a5a78); }
.s1-empty svg { width: 40px; height: 40px; stroke: currentColor; fill: none; stroke-width: 1.5; margin: 0 auto 12px; display: block; }
.s1-empty p { font-size: 14px; margin-bottom: 16px; }
.s1-empty-cta { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #6366f1; color: #fff; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; }
.s1-empty-cta svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2.5; }

/* ══════════════════════════════════════════════════════ ACCORDION */
.s1-accordion { display: flex; flex-direction: column; gap: 6px; }
.s1-acc-header-row { display: none; align-items: center; padding: 0 12px 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: var(--c1-t3, #5a5a78); }
@media (min-width: 640px) { .s1-acc-header-row { display: flex; } }
.s1-acc-col-agent  { flex: 2; min-width: 140px; display: flex; align-items: center; gap: 8px; }
.s1-acc-col-shift  { flex: 1.5; min-width: 90px; display: flex; flex-direction: column; gap: 2px; }
.s1-acc-col-status { flex: 1; min-width: 80px; }
.s1-acc-col-clock  { flex: 1; min-width: 70px; }
.s1-acc-col-dur    { flex: 0 0 50px; }
.s1-acc-col-actions{ flex-shrink: 0; display: flex; align-items: center; gap: 4px; padding-left: 6px; }
.s1-acc-row { background: var(--c1-surf, #13131a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 10px; overflow: hidden; transition: border-color .15s; }
.s1-acc-row:hover { border-color: rgba(99,102,241,.25); }
.s1-acc-row.s1-acc-all-ok { border-color: rgba(34,197,94,.2); }
.s1-acc-collapsed { display: flex; align-items: center; padding: 10px 12px; cursor: pointer; gap: 6px; flex-wrap: wrap; transition: background .1s; }
.s1-acc-collapsed:hover { background: rgba(255,255,255,.02); }
.s1-av { width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; position: relative; }
.s1-av-pulse { position: absolute; inset: -2px; border-radius: 50%; border: 2px solid #22c55e; animation: s1pulse 2s infinite; }
.s1-acc-name-block { display: flex; flex-direction: column; gap: 1px; }
.s1-acc-name { font-weight: 600; font-size: 13px; color: var(--c1-t1, #e8e8f0); }
.s1-acc-team { font-size: 10px; color: var(--c1-t3, #5a5a78); text-transform: capitalize; }
.s1-shift-badge { display: inline-block; font-size: 10px; font-weight: 700; font-family: 'JetBrains Mono', monospace; padding: 1px 5px; border-radius: 3px; }
.s1-shift-time { font-size: 11px; color: var(--c1-t2, #9090a8); font-family: 'JetBrains Mono', monospace; }
.s1-muted { color: var(--c1-t3, #5a5a78) !important; }
.s1-clock-val { font-size: 11px; color: var(--c1-t2, #9090a8); font-family: 'JetBrains Mono', monospace; }
.s1-dur { font-size: 11px; color: var(--c1-t1, #e8e8f0); font-family: 'JetBrains Mono', monospace; font-weight: 600; }

/* Status chips */
.s1-status-chip { display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; white-space: nowrap; }
.s1-status-on-time { background: rgba(34,197,94,.15); color: #4ade80; border: 1px solid rgba(34,197,94,.25); }
.s1-status-late    { background: rgba(234,179,8,.15); color: #fbbf24; border: 1px solid rgba(234,179,8,.25); }
.s1-status-early   { background: rgba(96,165,250,.15); color: #93c5fd; border: 1px solid rgba(96,165,250,.25); }
.s1-status-absent  { background: rgba(239,68,68,.15); color: #f87171; border: 1px solid rgba(239,68,68,.25); }
.s1-status-vac     { background: rgba(99,102,241,.15); color: #a5b4fc; border: 1px solid rgba(99,102,241,.25); }
.s1-status-pending { background: rgba(156,163,175,.1); color: var(--c1-t2, #9090a8); border: 1px solid var(--c1-brd, #1e1e2e); }
.s1-status-sched   { background: rgba(99,102,241,.15); color: #a5b4fc; border: 1px solid rgba(99,102,241,.25); }
.s1-status-none    { background: transparent; color: var(--c1-t3, #5a5a78); border: none; }

/* Row action buttons */
.s1-row-btn { width: 26px; height: 26px; border-radius: 6px; border: 1px solid var(--c1-brd, #1e1e2e); display: grid; place-items: center; cursor: pointer; transition: all .1s; flex-shrink: 0; }
.s1-row-btn svg { width: 13px; height: 13px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.s1-btn-detail:hover  { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.3); }
.s1-btn-detail:hover svg { stroke: #a5b4fc; }
.s1-btn-sched:hover   { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.3); }
.s1-btn-sched:hover svg { stroke: #4ade80; }
.s1-btn-approve:hover { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.3); }
.s1-btn-approve:hover svg { stroke: #4ade80; }
.s1-btn-switch:hover  { background: rgba(96,165,250,.1); border-color: rgba(96,165,250,.3); }
.s1-btn-switch:hover svg { stroke: #60a5fa; }
.s1-acc-chev { width: 22px; height: 22px; display: grid; place-items: center; transition: transform .2s; }
.s1-acc-chev.open { transform: rotate(180deg); }
.s1-acc-chev svg { width: 14px; height: 14px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2.5; }

/* ══════════════════════════════════════════════════════ EXPANDED DETAIL */
.s1-acc-detail { border-top: 1px solid var(--c1-brd, #1e1e2e); padding: 12px; background: rgba(0,0,0,.15); }
.s1-detail-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.s1-detail-shift-card {
  background: var(--c1-surf, #13131a); border: 1px solid var(--c1-brd, #1e1e2e);
  border-radius: 8px; padding: 10px; flex: 1; min-width: 140px; max-width: 200px;
}
.s1-dsc-top { display: flex; align-items: center; gap: 5px; margin-bottom: 8px; flex-wrap: wrap; }
.s1-dsc-day { font-size: 11px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }
.s1-dsc-badge { font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 3px; font-family: 'JetBrains Mono', monospace; }
.s1-dsc-approved { display: inline-flex; align-items: center; gap: 3px; font-size: 9px; color: #4ade80; margin-left: auto; }
.s1-dsc-approved svg { width: 10px; height: 10px; stroke: #4ade80; fill: none; stroke-width: 2.5; }
.s1-dsc-pending { font-size: 9px; color: var(--c1-t3, #5a5a78); margin-left: auto; }
.s1-dsc-rows { display: flex; flex-direction: column; gap: 4px; }
.s1-dsc-row { display: flex; justify-content: space-between; align-items: center; font-size: 11px; }
.s1-dsc-lbl { color: var(--c1-t3, #5a5a78); }
.s1-dsc-val { color: var(--c1-t1, #e8e8f0); font-family: 'JetBrains Mono', monospace; text-align: right; }
.s1-dsc-actions { margin-top: 8px; display: flex; gap: 4px; flex-wrap: wrap; }
.s1-dsc-btn { padding: 4px 8px; border-radius: 5px; font-size: 10px; font-weight: 600; border: 1px solid; cursor: pointer; transition: all .1s; }
.s1-dsc-btn-change   { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.3); color: #a5b4fc; }
.s1-dsc-btn-approve  { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.3); color: #4ade80; }
.s1-dsc-btn-unapprove{ background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.3); color: #f87171; }
.s1-dd-wrap { position: relative; }

/* Week summary bar */
.s1-week-summary { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-start; }
.s1-ws-bar { display: flex; gap: 4px; flex: 1; min-width: 280px; }
.s1-wb-cell { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.s1-wb-day { font-size: 9px; color: var(--c1-t3, #5a5a78); }
.s1-wb-slot { font-size: 9px; font-weight: 700; padding: 3px 4px; border-radius: 4px; text-align: center; font-family: 'JetBrains Mono', monospace; min-width: 28px; }
.s1-ws-totals { display: flex; gap: 8px; }
.s1-wst-item { background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; padding: 6px 10px; text-align: center; }
.s1-wst-val { font-size: 16px; font-weight: 700; color: var(--c1-t1, #e8e8f0); font-variant-numeric: tabular-nums; }
.s1-wst-lbl { font-size: 9px; color: var(--c1-t3, #5a5a78); margin-top: 2px; }

/* ══════════════════════════════════════════════════════ SHIFT COLOR CLASSES */
.s1-b-nit  { background: rgba(124,58,237,.18); border-color: rgba(124,58,237,.3); color: #a78bfa; }
.s1-b-mor  { background: rgba(37,99,235,.18); border-color: rgba(37,99,235,.3); color: #60a5fa; }
.s1-b-day  { background: rgba(22,163,74,.18); border-color: rgba(22,163,74,.3); color: #4ade80; }
.s1-b-eve  { background: rgba(234,88,12,.18); border-color: rgba(234,88,12,.3); color: #fb923c; }
.s1-b-vac  { background: rgba(99,102,241,.15); border-color: rgba(99,102,241,.25); color: #a5b4fc; }
.s1-b-ill  { background: rgba(239,68,68,.12); border-color: rgba(239,68,68,.2); color: #f87171; }
.s1-b-any  { background: rgba(156,163,175,.12); border-color: var(--c1-brd, #1e1e2e); color: var(--c1-t2, #9090a8); }
.s1-b-cus  { background: rgba(168,85,247,.15); border-color: rgba(168,85,247,.25); color: #c084fc; }
.s1-b-empty{ background: transparent; color: var(--c1-t3, #5a5a78); border-color: transparent; }

/* ══════════════════════════════════════════════════════ SLIDE PANELS */
.s1-slide-panel {
  position: fixed; top: 0; height: 100vh; width: 340px; max-width: 95vw;
  background: var(--c1-surf, #13131a); border: 1px solid var(--c1-brd, #1e1e2e);
  z-index: 500; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 0 40px rgba(0,0,0,.5);
}
.s1-slide-panel-right { right: 0; border-right: none; border-radius: 14px 0 0 14px; }
.s1-slide-panel-left  { left: 0; border-left: none; border-radius: 0 14px 14px 0; }
.s1-slide-right-enter-active, .s1-slide-right-leave-active { transition: transform .25s ease; }
.s1-slide-right-enter, .s1-slide-right-leave-to { transform: translateX(100%); }
.s1-slide-left-enter-active, .s1-slide-left-leave-active { transition: transform .25s ease; }
.s1-slide-left-enter, .s1-slide-left-leave-to { transform: translateX(-100%); }

.s1-sp-header { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid var(--c1-brd, #1e1e2e); flex-shrink: 0; }
.s1-sp-av { width: 38px; height: 38px; border-radius: 50%; display: grid; place-items: center; font-size: 14px; font-weight: 700; color: #fff; flex-shrink: 0; }
.s1-sp-hico { width: 20px; height: 20px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 1.5; flex-shrink: 0; }
.s1-sp-title-block { display: flex; flex-direction: column; gap: 2px; }
.s1-sp-title { font-size: 14px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }
.s1-sp-sub { font-size: 11px; color: var(--c1-t3, #5a5a78); }
.s1-sp-close { background: none; border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; padding: 4px; cursor: pointer; display: grid; place-items: center; flex-shrink: 0; }
.s1-sp-close svg { width: 14px; height: 14px; stroke: var(--c1-t3, #5a5a78); fill: none; stroke-width: 2; }
.s1-sp-body { flex: 1; overflow-y: auto; padding: 14px 16px 24px; }
.s1-sp-section-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--c1-t3, #5a5a78); margin-bottom: 8px; }

/* Agent detail panel */
.s1-sp-week-grid { display: flex; gap: 4px; }
.s1-sp-wg-cell { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; }
.s1-sp-wg-day { font-size: 9px; color: var(--c1-t3, #5a5a78); }
.s1-sp-wg-badge { font-size: 9px; font-weight: 700; padding: 3px 4px; border-radius: 4px; text-align: center; min-width: 28px; font-family: 'JetBrains Mono', monospace; }
.s1-sp-stats-row { display: flex; gap: 8px; margin-bottom: 4px; }
.s1-sp-stat { flex: 1; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 7px; padding: 8px; text-align: center; }
.s1-sp-stat-val { font-size: 18px; font-weight: 700; color: var(--c1-t1, #e8e8f0); font-variant-numeric: tabular-nums; }
.s1-sp-stat-lbl { font-size: 9px; color: var(--c1-t3, #5a5a78); margin-top: 2px; }
.s1-sp-punct { display: flex; flex-direction: column; gap: 6px; }
.s1-sp-punct-row { display: flex; align-items: center; gap: 8px; }
.s1-sp-punct-week { font-size: 11px; color: var(--c1-t3, #5a5a78); width: 80px; flex-shrink: 0; }
.s1-sp-punct-track { flex: 1; height: 8px; background: var(--c1-card, #17172a); border-radius: 4px; overflow: hidden; }
.s1-sp-punct-fill { height: 100%; border-radius: 4px; transition: width .4s ease; }
.s1-sp-punct-val { font-size: 11px; font-variant-numeric: tabular-nums; width: 36px; text-align: right; flex-shrink: 0; font-weight: 600; }
.s1-sp-actions { display: flex; flex-direction: column; gap: 8px; }
.s1-sp-btn { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; border: 1px solid; cursor: pointer; transition: all .1s; width: 100%; }
.s1-sp-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
.s1-sp-btn-approve { background: rgba(34,197,94,.12); border-color: rgba(34,197,94,.3); color: #4ade80; }
.s1-sp-btn-approve:hover { background: rgba(34,197,94,.2); }
.s1-sp-btn-reject  { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.25); color: #f87171; }
.s1-sp-btn-reject:hover { background: rgba(239,68,68,.18); }
.s1-sp-btn-switch  { background: rgba(96,165,250,.1); border-color: rgba(96,165,250,.25); color: #60a5fa; }
.s1-sp-btn-switch:hover { background: rgba(96,165,250,.18); }
.s1-sp-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Schedule panel */
.s1-sch-types { display: flex; flex-wrap: wrap; gap: 6px; }
.s1-sch-type-chip {
  display: flex; flex-direction: column; padding: 8px 10px;
  border-radius: 8px; cursor: pointer; border: 1px solid var(--c1-brd, #1e1e2e);
  background: var(--c1-card, #17172a); transition: all .1s; min-width: 90px;
}
.s1-sch-type-chip.on { border-color: rgba(99,102,241,.5); background: rgba(99,102,241,.12); }
.s1-sch-type-badge { font-size: 9px; font-weight: 800; font-family: 'JetBrains Mono', monospace; margin-bottom: 3px; }
.s1-sch-type-name { font-size: 12px; font-weight: 600; color: var(--c1-t1, #e8e8f0); }
.s1-sch-type-time { font-size: 10px; color: var(--c1-t3, #5a5a78); }
.s1-sch-days { display: flex; flex-wrap: wrap; gap: 5px; }
.s1-sch-day-chip { padding: 6px 10px; border-radius: 7px; border: 1px solid var(--c1-brd, #1e1e2e); background: var(--c1-card, #17172a); cursor: pointer; text-align: center; transition: all .1s; }
.s1-sch-day-chip.on { border-color: rgba(99,102,241,.5); background: rgba(99,102,241,.12); }
.s1-sch-day-name { font-size: 11px; font-weight: 700; color: var(--c1-t1, #e8e8f0); }
.s1-sch-day-date { font-size: 9px; color: var(--c1-t3, #5a5a78); font-family: 'JetBrains Mono', monospace; }
.s1-sch-recurring { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--c1-t2, #9090a8); }
.s1-sch-tog { width: 32px; height: 18px; border-radius: 9px; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); cursor: pointer; position: relative; transition: background .2s; }
.s1-sch-tog.on { background: #6366f1; border-color: #6366f1; }
.s1-sch-tog-dot { position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: var(--c1-t3, #5a5a78); transition: transform .2s, background .2s; }
.s1-sch-tog.on .s1-sch-tog-dot { transform: translateX(14px); background: #fff; }
.s1-sch-notes { width: 100%; box-sizing: border-box; background: var(--c1-card, #17172a); border: 1px solid var(--c1-brd, #1e1e2e); border-radius: 6px; padding: 8px 10px; color: var(--c1-t1, #e8e8f0); font-size: 12px; font-family: inherit; resize: vertical; outline: none; }
.s1-sch-notes::placeholder { color: var(--c1-t3, #5a5a78); }
.s1-sch-submit { margin-top: 16px; }

/* ══════════════════════════════════════════════════════ OVERLAY */
.s1-overlay { position: fixed; inset: 0; z-index: 490; background: rgba(0,0,0,.4); }

/* ══════════════════════════════════════════════════════ ATTENDANCE SUMMARY BAR */
.s1-attendance-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px; flex-shrink: 0;
  background: var(--c1-card, #17172a);
  border-bottom: 1px solid var(--c1-brd, #1e1e2e);
  overflow-x: auto;
}
.s1-att-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 16px;
  font-size: 11px; font-weight: 500;
  color: var(--c1-t2, #9090a8);
  background: rgba(255,255,255,.04);
  border: 1px solid var(--c1-brd, #1e1e2e);
  white-space: nowrap;
}
.s1-att-pill strong { font-weight: 800; color: var(--c1-t1, #e8e8f0); }
.s1-att-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
}
.s1-att-ontime strong { color: #22c55e; }
.s1-att-late strong { color: #f59e0b; }
.s1-att-absent strong { color: #ef4444; }
.s1-att-early strong { color: #3b82f6; }

.s1-att-export {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px; border-radius: 7px;
  font-size: 11px; font-weight: 600;
  color: #6366f1; background: rgba(99,102,241,.08);
  border: 1px solid rgba(99,102,241,.2);
  cursor: pointer; transition: all .15s; white-space: nowrap;
  flex-shrink: 0;
}
.s1-att-export:hover { background: rgba(99,102,241,.15); border-color: rgba(99,102,241,.4); }
.s1-att-export svg { stroke: currentColor; }

/* Approve All - prominent */
.s1-qa-approve-all {
  background: linear-gradient(135deg, #22c55e, #16a34a) !important;
  color: #fff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  padding: 8px 18px !important;
  border-radius: 8px !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(34,197,94,.35);
  text-transform: uppercase;
  letter-spacing: .5px;
  transition: all .2s;
}
.s1-qa-approve-all:hover {
  background: linear-gradient(135deg, #16a34a, #15803d) !important;
  box-shadow: 0 4px 14px rgba(34,197,94,.45);
  transform: translateY(-1px);
}

/* Panel uses global c1-design.css widths: 88vw mobile, 380px desktop */

/* ── Viki left panel ─────────────────────────────────────────── */
.s1-viki-slide-enter-active, .s1-viki-slide-leave-active { transition: transform .28s cubic-bezier(.4,0,.2,1); }
.s1-viki-slide-enter, .s1-viki-slide-leave-to { transform: translateX(-100%); }
.s1-viki-left-panel {
  position: fixed;
  top: 0; left: 0; bottom: 0;
  width: 300px; max-width: 92vw;
  background: var(--c1-bg, #F7F6F3);
  z-index: 1100;
  display: flex; flex-direction: column;
  box-shadow: 4px 0 32px rgba(0,0,0,.22);
  overflow: hidden;
}

/* ── TEAM VIEW TOGGLE ──────────────────────────────────────── */
.sh1-team-toggle-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 6px;
  background: var(--c1-bg, #f7f6f3);
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.sh1-tv-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 13px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  border: 1.5px solid rgba(0,0,0,.13);
  background: transparent;
  color: var(--c1-t2, #444);
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}
.sh1-tv-btn.on {
  border-color: rgba(0,0,0,.3);
  background: rgba(0,0,0,.07);
  color: var(--c1-t1, #111);
}
.sh1-tv-btn.sh1-tv-t1.on {
  background: rgba(14,116,144,.12);
  border-color: #0e7490;
  color: #0e7490;
}
.sh1-tv-btn.sh1-tv-t2.on {
  background: rgba(124,58,237,.12);
  border-color: #7c3aed;
  color: #7c3aed;
}
.sh1-tv-btn:hover:not(.on) {
  border-color: rgba(0,0,0,.22);
  background: rgba(0,0,0,.04);
}

/* ── TEAM SUMMARY BAR ─────────────────────────────────────── */
.sh1-team-summary-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 14px;
  font-size: 11px;
  color: var(--c1-t2, #555);
  background: var(--c1-bg, #f7f6f3);
  border-bottom: 1px solid rgba(0,0,0,.05);
}
.sh1-tsb-item {
  display: flex;
  align-items: center;
  gap: 5px;
}
.sh1-tsb-item strong { font-weight: 700; }
.sh1-tsb-item.sh1-tsb-t1 strong { color: #0e7490; }
.sh1-tsb-item.sh1-tsb-t2 strong { color: #7c3aed; }
.sh1-tsb-dot {
  width: 8px; height: 8px; border-radius: 50%;
  flex-shrink: 0;
}
.sh1-tsb-t1 .sh1-tsb-dot { background: #0e7490; }
.sh1-tsb-t2 .sh1-tsb-dot { background: #7c3aed; }
.sh1-tsb-sep { color: rgba(0,0,0,.2); }

/* ── TEAM COLOR SHIFT ROWS ────────────────────────────────── */
.sh1-team1-row {
  border-left: 3px solid #0e7490 !important;
  background: rgba(14,116,144,.03) !important;
}
.sh1-team2-row {
  border-left: 3px solid #7c3aed !important;
  background: rgba(124,58,237,.03) !important;
}
</style>
